import React, { useRef, useMemo, useState, useCallback, useImperativeHandle, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
} from '@tanstack/react-table';
import { useTheme } from 'styled-components';
import { TanStackGridWrap, TanStackTable, TanStackTh, TanStackTr, TanStackTd, EditorCellWrap, HiGridHeaderScrollArea, ROW_HEIGHT } from './style/StandardTanStackGrid.styles';
import { useVirtualizer } from '@tanstack/react-virtual';
import { buildTableColumns, normalizeDateForCompare, isHiGridSystemColumnId } from './core/tanstackGridColumns';
import { buildHeaderGrid, renderMergedHeaderRows } from './core/headerMergeUtils';
import { buildBodyMergeMap, resolveBodyMerge } from './core/bodyMergeUtils';
import { useGridCellFocus } from './core/useGridCellFocus';
import ExcelColumnFilterButton from './ExcelColumnFilterButton';
import GridContextMenu from './GridContextMenu';
import GridFindReplaceDialog from './GridFindReplaceDialog';
import GridColumnVisibilityPopup, { GridHeaderContextMenu } from './GridColumnVisibilityPopup';
import GridFilterStatusBar, { buildFilterChips } from './GridFilterStatusBar';
import GridMergeColumnsPopup from './GridMergeColumnsPopup';
import GridPivotDialog from './GridPivotDialog';
import { loadGridPrefs, saveGridPrefs, normalizeColumnSizing } from './core/gridLocalPrefs';
import { computeSelectionStats, formatStatNumber } from './core/gridSelectionStats';
import { downloadGridXlsx, downloadGridCsv } from './core/gridQuickExport';
import { normalizeColumnOrder, moveColumnInOrder, wouldCrossFreezeBoundary } from './core/gridColumnOrder';
import {
  createRuleId,
  formatListFilterChip,
  formatRuleChip,
  inferColumnKind,
  normalizeRule,
  resolveFormatColor,
  rowMatchesAllHideRules,
} from './core/gridConditionalRules';
import {
  TextCellEditor,
  NumberCellEditor,
  DateCellEditor,
  ComboCellEditor,
  CheckboxCellEditor,
} from './Editor';
import { showAlert, showConfirm } from 'utils/toastBridge';


/**
 * HiGridComponent (hiGrid 그리드 컴포넌트)
 *
 * - ref로 getSelectedRowIndex(), setSelectedRowIndex(idx) 노출 → CRUD 훅에서 gridRef로 공유
 * - 선택 행은 내부 focusedCell에서 파생되며, selectedRowIndex/onSelectionChange는 선택 사항(호환용)
 *
 * - data는 내부 state로 관리, ref로 getData() / setData(fn|value) 노출
 * @param {number|null} [props.selectedRowIndex] - (선택) 부모가 제어할 때만 사용
 * @param {Function} [props.onSelectionChange] - (선택) (rowIndex) => void
 * @param {Array} props.columns - TanStack 컬럼 정의
 * @param {string|number} props.height - 그리드 높이
 * @param {boolean} props.showRowNumber - No. 컬럼 표시
 * - gridEvents: useHiGridHook의 mstGrid.onXXX = fn 으로 할당 (ref.gridEventsRef)
 * @param {Function} [gridEvents.onSelectRow] - (row, rowIndex) => void. 행 선택 시
 * @param {Function} [gridEvents.onSelectCell] - (row, cell) => void. 셀 선택/포커스 시. cell = { field }
 * @param {Function} [gridEvents.onBeforeEditCell] - (row, cell) => void. 편집 모드 진입 직전
 * @param {Function} [gridEvents.onAfterEditCell] - (row, cell) => void. 편집 모드 진입 직후
 * @param {Function} [gridEvents.onBeforeSaveCell] - (row, cell) => void. 셀 저장 직전. cell = { field, value }
 * @param {Function} [gridEvents.onAfterSaveCell] - (row, cell) => void. 셀 저장 직후. cell = { field, value }
 * @param {Function} [gridEvents.onInitialize] - () => void. 그리드 마운트 완료 시
 * @param {Function} [gridEvents.onGridClicked] - ({ data, rowIndex }) => void. 행 클릭 시
 * @param {Function} [gridEvents.onGridCellClicked] - (rowData, { field }, rowIndex) => void. 셀 클릭 시
 * @param {Function} [gridEvents.onGridCellDoubleClicked] - (rowData, { field }, rowIndex) => void. 셀 더블클릭 시
 */
const HiGridComponent  = React.forwardRef(({
  selectedRowIndex: selectedRowIndexProp = null,
  onSelectionChange,
  columns: dataColumns = [],
  height = '100%',
  showRowNumber = true,
  /** No. 컬럼 폭(px). 기본 40 */
  rowNumberSize = 40,
  /** false면 A/U/D 상태(파란 펜 등) 컬럼 숨김. 기본 true */
  showModColumn = true,
  /**
   * 행 멀티체크 컬럼 (mod와 No. 사이).
   * 미지정 시 showModColumn 과 동일 — CRUD(상태열 표시) 그리드에 기본 활성.
   * true면 mod·No.도 강제 표시 (순서: mod → 체크 → No.)
   */
  enableRowCheck: enableRowCheckProp,
  /** 헤더 셀 정렬: 'left' | 'center' | 'right', 기본값 center */
  headerAlign = 'center',
  /** true: 그리드가 가로 전체 꽉 참, false: 컬럼 size 합만큼만 너비 사용·나머지 여백 (기본값 false) */
  fullWidth = false,
  /**
   * 옵션: 병합 헤더 (다중 행/열). 지정 시 thead를 이 구조로 렌더.
   * - headerRows: 2차원 배열. 각 행은 { label, colSpan?, rowSpan? } 셀 배열.
   * - 마지막 행의 총 컬럼 수는 columns(데이터 컬럼) 개수와 일치해야 함.
   * @type {Array<Array<{ label: string, colSpan?: number, rowSpan?: number, align?: string }>>}
   */
  headerRows = null,
  /**
   * 옵션: 트리 그리드. 지정 시 getSubRows/getExpandedRowModel 사용, 데이터는 루트 배열(각 노드 subRows).
   * @type {{ idKey: string, parentKey?: string, childrenKey?: string }}
   */
  treeOptions = null,
  /** true: 가상 스크롤 적용(대량 데이터 시 성능 개선). 트리 모드에서는 미적용. 기본값 true */
  useVirtualRows = true,
  /**
   * true: 엑셀형 열 필터(헤더 깔때기) 활성화. 로드된 데이터에 대해 클라이언트 멀티체크 필터.
   * 트리 모드에서는 미적용.
   */
  enableColumnFilters = false,
  /** 헤더 드래그로 열 순서 변경 (표시만). 트리에서는 미적용 */
  enableColumnReorder = true,
  /**
   * 사용자가 동일 값 병합 on/off·열 지정. 미지정 시 조회 그리드(행편집 메뉴 없음)만 활성.
   */
  enableUserMerge: enableUserMergeProp,
  /**
   * 행/열/값 피벗. 미지정 시 조회 그리드만 활성.
   */
  enablePivot: enablePivotProp,
  /** 우클릭 컨텍스트 메뉴 */
  enableContextMenu = false,
  /** 행 추가(위/아래)·삭제·복제 메뉴 */
  enableRowEditMenu = false,
  /** 잘라내기 메뉴/단축키 */
  enableCut = true,
  /** 찾기/바꾸기 메뉴 */
  enableFindReplace = true,
  /** 바꾸기(값 수정) 허용. false면 찾기만 */
  allowReplace = true,
  onContextAddAbove,
  onContextAddBelow,
  onContextDelete,
  onContextDuplicate,
  /** localStorage 설정 키. 지정 시 열숨김·고정이 로그아웃 후에도 유지 */
  gridId = null,
  /** 사용자별 설정 분리 (보통 login id). 없으면 anon */
  prefsUserId = null,
  /**
   * 최초 방문(prefs에 hiddenColumns 없음) 시 기본 숨김 컬럼 id.
   * 사용자가 열숨김을 한 번이라도 저장하면 prefs를 따름.
   */
  defaultHiddenColumnIds = null,
  /** 헤더 우클릭: 열숨김/열고정/행고정 */
  enableHeaderMenu = true,
  /** 선택 영역 숫자 집계(합계·평균·건수) */
  enableSelectionStats = true,
  /** 행/열 고정 */
  enableFreeze = true,
  /**
   * 우클릭 → 내보내기(.xlsx / .csv). 조회된 그리드 데이터 전체, 컬럼 선택 UI 없음.
   */
  enableQuickExport = true,
  /** 빠른 내보내기 기본 파일명 (확장자 제외) */
  exportFileName = 'grid_export',
  /**
   * true: 컬럼(필드) 헤더 행을 활성 강조색으로 표시 (멀티그리드 Alt+F2 선택 등)
   */
  headerActive = false,
  /** mstGrid.colOptions. setColOption으로 설정한 컬럼별 readOnly 등 오버라이드 */
  colOptions = {},
  /** 행 높이(px). 미지정 시 기본 28, stackPair 컬럼이 있으면 52 */
  rowHeight: rowHeightProp = null,
}, ref) => {
  // ThemeProvider 런타임 테마 (정적 theme import 대신)
  const theme = useTheme();
  const emptyGridEventsRef = useRef({});
  const gridEvents = ref?.gridEventsRef?.current ?? emptyGridEventsRef.current;
  const rowsRef = useRef([]); // 행 데이터 저장 (체크·키보드 이동 등에서 참조)

  const hasStackPairCol = useMemo(
    () => (dataColumns || []).some((c) => c?.meta?.stackPair),
    [dataColumns]
  );
  /** CRUD(상태열) 그리드는 기본 행체크 ON — 조회 화면은 enableRowCheck={false} */
  const enableRowCheck =
    enableRowCheckProp !== undefined ? !!enableRowCheckProp : !!showModColumn;
  // showModColumn={false}면 체크만 쓰고 상태열은 숨김 (코드조회 팝업 등)
  const effectiveShowModColumn = !!showModColumn;
  const effectiveShowRowNumber = enableRowCheck ? true : !!showRowNumber;
  const rowHeight = rowHeightProp != null
    ? Number(rowHeightProp) || ROW_HEIGHT
    : (hasStackPairCol ? 52 : ROW_HEIGHT);

  /** 행 멀티체크 (row id Set) — 데이터 필드가 아님 */
  const [checkedRowIds, setCheckedRowIds] = useState(() => new Set());
  const checkedRowIdsRef = useRef(checkedRowIds);
  checkedRowIdsRef.current = checkedRowIds;

  const isRowChecked = useCallback((rowId) => checkedRowIdsRef.current.has(rowId), []);
  const toggleRowCheck = useCallback((rowId) => {
    if (rowId == null) return;
    setCheckedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);
  const checkAllVisibleRows = useCallback((checked) => {
    const list = rowsRef.current || [];
    setCheckedRowIds(() => {
      if (!checked) return new Set();
      return new Set(list.map((r) => r.id));
    });
  }, []);
  const clearRowChecks = useCallback(() => setCheckedRowIds(new Set()), []);
  const getCheckedOriginalRows = useCallback(() => {
    const idSet = checkedRowIdsRef.current;
    if (!idSet.size) return [];
    return (rowsRef.current || [])
      .filter((r) => idSet.has(r.id))
      .map((r) => r.original)
      .filter(Boolean);
  }, []);

  /** meta.editable + colOptions[field].readOnly 반영한 편집 가능 여부 */
  const getIsEditable = useCallback((col, rowData, colOpts) => {
    const meta = col?.meta;
    const ed = meta?.editable;
    // 셀 렌더러(Boolean)와 동일 기준 — ed === true 만 허용하면 편집 진입이 막힐 수 있음
    const base = typeof ed === 'function' ? !!ed(rowData) : Boolean(ed);
    const field = col?.id ?? col?.accessorKey;
    const opt = field && colOpts?.[field];
    if (opt?.readOnly !== undefined) {
      const ro = typeof opt.readOnly === 'function' ? opt.readOnly(rowData) : opt.readOnly;
      return base && !ro;
    }
    return base;
  }, []);

  /** 선택 행 > 읽기전용 배경 > 기본(행 hover 상속)
   * theme 반드시 deps에 포함 — 다크↔라이트 전환 후 stale readonlyBg(#111827)가 남는 것 방지 */
  const getCellBackgroundColor = useCallback((rowIndex, colDef, rowData, selectedIdx) => {
    if (selectedIdx != null && rowIndex === selectedIdx) {
      return theme.colors.grid.rowSelected;
    }
    const colId = colDef?.id ?? colDef?.accessorKey;
    // 체크·No·상태열은 행 배경 유지 (고정열에 읽기전용 톤이 붙어 검은 덩어리로 보이던 문제)
    if (isHiGridSystemColumnId(colId)) {
      return undefined;
    }
    const formatBg = resolveFormatColor(conditionalRulesRef.current, colId, rowData, theme);
    if (formatBg) return formatBg;
    const editable = getIsEditable(colDef, rowData, colOptions);
    if (editable === false) return theme.colors.grid.readonlyBg;
    return undefined;
  }, [getIsEditable, colOptions, theme]);

  const [data, setData] = useState([]);
  const [editingCell, setEditingCell] = useState({ rowId: null, field: null, initialKey: undefined });
  const [showCopiedBorder, setShowCopiedBorder] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [columnSizingInfo, setColumnSizingInfo] = useState({}); // 리사이즈 중 deltaOffset 등 (미리보기용)
  const [sorting, setSorting] = useState([]); // 헤더 클릭 정렬 [{ id: columnId, desc: boolean }]
  const [columnFilters, setColumnFilters] = useState([]); // 엑셀형 열 필터
  const [scrollLeft, setScrollLeft] = useState(0); // 그리드 가로 스크롤 (미리보기 라인 위치 보정용)
  /** 바디 세로 스크롤바 폭 — 헤더에 동일 padding 줘 열 정렬 맞춤 */
  const [bodyScrollbarWidth, setBodyScrollbarWidth] = useState(0);
  /** 스택 컬럼(월·분기)에서 현재 포커스된 상/하 필드 — 반칸만 강조 */
  const [stackFocus, setStackFocus] = useState(null); // { rowId, field }
  const [contextMenu, setContextMenu] = useState(null); // { x, y }
  const [headerMenu, setHeaderMenu] = useState(null); // { x, y, columnId, columnLabel }
  const [columnVisPopup, setColumnVisPopup] = useState(null); // { x, y }
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const [findReplaceMeta, setFindReplaceMeta] = useState({
    initialScope: 'column',
    hasSelectionSnapshot: false,
  });
  const findCursorRef = useRef({ rowIdx: -1, colIdx: -1 });
  /** 찾기/바꾸기 열 때 블록을 스냅샷 — 이후 selectionRange가 바뀌어도 '선택 영역' 유지 */
  const findReplaceSelectionRef = useRef(null);

  const initialPrefs = useMemo(() => loadGridPrefs(gridId, prefsUserId), [gridId, prefsUserId]);
  const [columnSizing, setColumnSizing] = useState(() =>
    normalizeColumnSizing(loadGridPrefs(gridId, prefsUserId).columnSizing)
  );
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const prefs = loadGridPrefs(gridId, prefsUserId);
    // prefs.hiddenColumns 가 배열이면 사용자 설정 존중, 없으면 화면 defaultHidden
    const hidden = Array.isArray(prefs.hiddenColumns)
      ? prefs.hiddenColumns
      : Array.isArray(defaultHiddenColumnIds)
        ? defaultHiddenColumnIds
        : [];
    const vis = {};
    hidden.forEach((id) => {
      vis[id] = false;
    });
    return vis;
  });
  const [freezeToColumnId, setFreezeToColumnId] = useState(initialPrefs.freezeToColumnId ?? null);
  const [freezeRowCount, setFreezeRowCount] = useState(Number(initialPrefs.freezeRowCount) || 0);
  const [columnOrder, setColumnOrder] = useState(() =>
    Array.isArray(initialPrefs.columnOrder) ? initialPrefs.columnOrder : []
  );
  const [mergeEnabled, setMergeEnabled] = useState(() => {
    if (initialPrefs.mergeTouched) return Boolean(initialPrefs.mergeEnabled);
    return (dataColumns || []).some((c) => c?.meta?.mergeRows);
  });
  const [mergeColumnIds, setMergeColumnIds] = useState(() => {
    if (Array.isArray(initialPrefs.mergeColumnIds)) return initialPrefs.mergeColumnIds;
    return (dataColumns || [])
      .filter((c) => c?.meta?.mergeRows)
      .map((c) => c.id ?? c.accessorKey ?? c.field)
      .filter(Boolean);
  });
  const [mergeTouched, setMergeTouched] = useState(Boolean(initialPrefs.mergeTouched));
  const [conditionalRules, setConditionalRules] = useState(() =>
    Array.isArray(initialPrefs.conditionalRules) ? initialPrefs.conditionalRules : []
  );
  const [pivotOpen, setPivotOpen] = useState(false);
  const [mergePopup, setMergePopup] = useState(null);
  const headerDragRef = useRef({ from: null, moved: false });
  const conditionalRulesRef = useRef(conditionalRules);
  conditionalRulesRef.current = conditionalRules;

  const persistPrefs = useCallback(
    (patch) => {
      if (!gridId) return;
      saveGridPrefs(gridId, prefsUserId, patch);
    },
    [gridId, prefsUserId]
  );

  useEffect(() => {
    if (!gridId) return;
    const hiddenColumns = Object.entries(columnVisibility)
      .filter(([, v]) => v === false)
      .map(([id]) => id)
      .filter((id) => !isHiGridSystemColumnId(id));
    persistPrefs({ hiddenColumns });
  }, [columnVisibility, gridId, persistPrefs]);

  useEffect(() => {
    if (!gridId) return;
    persistPrefs({ freezeToColumnId: freezeToColumnId || null, freezeRowCount: freezeRowCount || 0 });
  }, [freezeToColumnId, freezeRowCount, gridId, persistPrefs]);

  /** 헤더 드래그로 조절한 컬럼 폭 유지 (columnResizeMode=onEnd 후 반영) */
  useEffect(() => {
    if (!gridId) return;
    persistPrefs({ columnSizing: normalizeColumnSizing(columnSizing) });
  }, [columnSizing, gridId, persistPrefs]);

  useEffect(() => {
    if (!gridId || !columnOrder.length) return;
    persistPrefs({ columnOrder });
  }, [columnOrder, gridId, persistPrefs]);

  useEffect(() => {
    if (!gridId) return;
    persistPrefs({ mergeEnabled, mergeColumnIds, mergeTouched });
  }, [mergeEnabled, mergeColumnIds, mergeTouched, gridId, persistPrefs]);

  useEffect(() => {
    if (!gridId) return;
    persistPrefs({ conditionalRules: (conditionalRules || []).filter((r) => r.persist) });
  }, [conditionalRules, gridId, persistPrefs]);

  const isTree = Boolean(treeOptions);
  const columnFiltersEnabled = Boolean(enableColumnFilters) && !isTree;
  const columnReorderEnabled = Boolean(enableColumnReorder) && !isTree;
  const enableUserMerge =
    enableUserMergeProp !== undefined ? !!enableUserMergeProp : !enableRowEditMenu && !isTree;
  const enablePivot =
    enablePivotProp !== undefined ? !!enablePivotProp : !enableRowEditMenu && !isTree;
  const developerMergeIds = useMemo(
    () =>
      (dataColumns || [])
        .filter((c) => c?.meta?.mergeRows)
        .map((c) => c.id ?? c.accessorKey ?? c.field)
        .filter(Boolean),
    [dataColumns]
  );
  const effectiveMergeIds = useMemo(
    () => (enableUserMerge ? (mergeEnabled ? mergeColumnIds : []) : developerMergeIds),
    [enableUserMerge, mergeEnabled, mergeColumnIds, developerMergeIds]
  );
  const hasBodyMerge = effectiveMergeIds.length > 0;
  const hideRules = useMemo(
    () => (conditionalRules || []).filter((r) => r.hide),
    [conditionalRules]
  );
  const setDataRef = useRef(setData);
  const dataRef = useRef(data);
  const editingCellRef = useRef(editingCell);
  const gridWrapRef = useRef(null);
  const headerScrollRef = useRef(null); // 헤더 가로 스크롤 동기화용
  const footerScrollRef = useRef(null); // 푸터 가로 스크롤 동기화용
  const tableApiRef = useRef(null); // 필터된 행 조회용
  setDataRef.current = setData;
  dataRef.current = data;
  editingCellRef.current = editingCell;

  /** 그리드 마운트 완료 시 onInitialize 호출 */
  useEffect(() => {
    gridEvents.onInitialize?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    focusedCell, // 포커스된 셀 정보
    setFocusedCell, // 포커스된 셀 설정
    selectionRange, // 선택 영역 정보
    setSelectionRange, // 선택 영역 제거용 (트리 모드에서 범위 선택 비사용)
    isCellFocused, // 셀 포커스 여부
    isCellInSelection, // 셀 선택 여부
    getSelectionEdgeVars, // 블럭 외곽 가이드 CSS 변수
    handleKeyDown: handleFocusKeyDown, // 포커스 키 이벤트 핸들러
    handleCellMouseDown, // 셀 마우스 다운 이벤트 핸들러
    handleCellMouseEnter, // 셀 마우스 엔터 이벤트 핸들러
    moveFocusedCellHorizontal, // 포커스 셀 수평 이동 핸들러 
    moveFocusedCellVertical, // 포커스 셀 수직 이동 핸들러
  } = useGridCellFocus();

  const columnIdsRef = useRef([]); // 컬럼 아이디 저장
  const columnsRef = useRef([]); // 컬럼 데이터 저장
  const activeCellRef = useRef(null); // 활성 셀 저장
  /** setCellValue 연속 호출 시 한 번에 반영하기 위한 큐 */
  const pendingSetCellValueQueueRef = useRef([]);
  const setCellValueFlushScheduledRef = useRef(false);

  /** 편집 중 맨 앞/맨 끝에서 ←/→ 누를 때 셀 이동 후 포커스 */
  const onBoundaryArrow = useCallback(
    (direction) => {
      setEditingCell({ rowId: null, field: null, initialKey: undefined });
      moveFocusedCellHorizontal(direction, rowsRef.current, columnIdsRef.current, onSelectionChange);
      gridWrapRef.current?.focus();
    },
    [moveFocusedCellHorizontal, onSelectionChange]
  );

  /** Enter 시 저장 후 아래 셀로 이동 / Shift+Enter 시 위 셀로 이동 */
  const focusWrapIfStillHere = useCallback(() => {
    setTimeout(() => {
      const wrap = gridWrapRef.current;
      if (!wrap) return;
      const ae = document.activeElement;
      // 다른 그리드·외부로 포커스가 이미 이동했으면 가로채지 않음
      if (ae && ae !== document.body && !wrap.contains(ae)) return;
      if (editingCellRef.current?.rowId != null) return;
      wrap.focus({ preventScroll: true });
    }, 0);
  }, []);

  const onEnterMoveDown = useCallback(() => {
    setEditingCell({ rowId: null, field: null, initialKey: undefined });
    moveFocusedCellVertical('down', rowsRef.current, columnIdsRef.current, onSelectionChange);
    focusWrapIfStillHere();
  }, [moveFocusedCellVertical, onSelectionChange, focusWrapIfStillHere]);

  const onEnterMoveUp = useCallback(() => {
    setEditingCell({ rowId: null, field: null, initialKey: undefined });
    moveFocusedCellVertical('up', rowsRef.current, columnIdsRef.current, onSelectionChange);
    focusWrapIfStillHere();
  }, [moveFocusedCellVertical, onSelectionChange, focusWrapIfStillHere]);

  const columns = useMemo( // 컬럼 데이터 생성
    () =>
      buildTableColumns(
        dataColumns,
        effectiveShowRowNumber,
        treeOptions,
        rowNumberSize,
        effectiveShowModColumn,
        enableRowCheck
      ),
    [dataColumns, effectiveShowRowNumber, treeOptions, rowNumberSize, effectiveShowModColumn, enableRowCheck]
  );

  useEffect(() => {
    const ids = columns.map((c) => c.id).filter(Boolean);
    setColumnOrder((prev) => normalizeColumnOrder(prev.length ? prev : initialPrefs.columnOrder, ids));
    // 컬럼 구성이 바뀔 때만 정규화
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  /** Tab / Shift+Tab: 저장 후 오른쪽/왼쪽 입력 가능 셀로 이동하며 편집 모드로 열기 */
  const onTabMove = useCallback(
    (direction) => {
      const rows = rowsRef.current;
      const columnIds = columnIdsRef.current;
      if (!rows?.length || !columnIds?.length) return;
      const { rowId, field } = editingCell ?? {};
      if (rowId == null || field == null) return;
      let rowIdx = rows.findIndex((r) => r.id === rowId);
      let colIdx = columnIds.indexOf(field);
      if (rowIdx < 0) rowIdx = 0;
      if (colIdx < 0) colIdx = 0;
      const isEditable = (cid, rowData) => {
        const col = columns.find((c) => c.id === cid);
        return getIsEditable(col, rowData, colOptions);
      };
      let nextRowIdx = rowIdx;
      let nextColIdx = colIdx;
      const totalCells = rows.length * columnIds.length;
      let steps = 0;
      while (steps <= totalCells) {
        if (direction === 'right') {
          nextColIdx += 1;
          if (nextColIdx >= columnIds.length) {
            nextRowIdx += 1;
            nextColIdx = 0;
          }
        } else {
          nextColIdx -= 1;
          if (nextColIdx < 0) {
            nextRowIdx -= 1;
            nextColIdx = columnIds.length - 1;
          }
        }
        nextRowIdx = Math.max(0, Math.min(nextRowIdx, rows.length - 1));
        nextColIdx = Math.max(0, Math.min(nextColIdx, columnIds.length - 1));
        const nextField = columnIds[nextColIdx];
        const nextRow = rows[nextRowIdx];
        if (nextRow && isEditable(nextField, nextRow.original)) {
          setEditingCell({ rowId: null, field: null, initialKey: undefined });
          setFocusedCell({ rowId: nextRow.id, field: nextField });
          setEditingCell({ rowId: nextRow.id, field: nextField });
          onSelectionChange?.(nextRow.index);
          return;
        }
        if (nextRowIdx === rowIdx && nextColIdx === colIdx) return;
        rowIdx = nextRowIdx;
        colIdx = nextColIdx;
        steps += 1;
      }
    },
    [editingCell, columns, setFocusedCell, onSelectionChange, getIsEditable, colOptions]
  );

  /** 선택 영역을 주어진 값으로 채움 (editable && !skipPaste 컬럼만) */
  const fillSelectionWithValue = useCallback(
    (value, { allowMultiColumn = false } = {}) => {
      if (!selectionRange) return false;
      const rows = rowsRef.current;
      const columnIds = columnIdsRef.current;
      const cols = columnsRef.current;
      if (!rows?.length || !columnIds?.length) return false;
      if (!allowMultiColumn && selectionRange.endField !== selectionRange.startField) {
        showAlert('선택 영역이 여러 컬럼을 포함하고 있습니다. 한 개의 컬럼만 선택해주세요.', 'error');
        return false;
      }
      const rowIds = rows.map((r) => r.id);
      const rMin = Math.min(
        rowIds.indexOf(selectionRange.startRowId),
        rowIds.indexOf(selectionRange.endRowId)
      );
      const rMax = Math.max(
        rowIds.indexOf(selectionRange.startRowId),
        rowIds.indexOf(selectionRange.endRowId)
      );
      const cMin = Math.min(
        columnIds.indexOf(selectionRange.startField),
        columnIds.indexOf(selectionRange.endField)
      );
      const cMax = Math.max(
        columnIds.indexOf(selectionRange.startField),
        columnIds.indexOf(selectionRange.endField)
      );
      if (rMin < 0 || cMin < 0) return false;

      const isPasteable = (field, rowData) => {
        const col = cols?.find((c) => c.id === field);
        if (col?.meta?.skipPaste === true) return false;
        return getIsEditable(col, rowData, colOptions);
      };

      const onBefore = gridEvents.onBeforeSaveCell;
      const onAfter = gridEvents.onAfterSaveCell;
      setDataRef.current?.((prev) => {
        const next = prev ? [...prev] : [];
        for (let rIdx = rMin; rIdx <= rMax; rIdx++) {
          for (let cIdx = cMin; cIdx <= cMax; cIdx++) {
            const field = columnIds[cIdx];
            if (!next[rIdx]) continue;
            if (!isPasteable(field, next[rIdx])) continue;
            onBefore?.(next[rIdx], { field, value });
            next[rIdx] = { ...next[rIdx], [field]: value };
            const row = next[rIdx];
            if (row.mod !== 'A' && row.mod !== 'D') row.mod = 'U';
            onAfter?.(row, { field, value });
          }
        }
        return next;
      });
      return true;
    },
    [selectionRange, gridEvents, getIsEditable, colOptions]
  );

  /** Ctrl+Enter: 선택 영역의 모든 셀을 주어진 값으로 채움 — 한 컬럼만 대상 */
  const handleFillSelection = useCallback(
    (value) => {
      fillSelectionWithValue(value, { allowMultiColumn: false });
    },
    [fillSelectionWithValue]
  );

  /** 트리에서 rowId(idKey 또는 _tempId)에 해당하는 노드 찾기 */
  const findNodeInTree = useCallback((nodes, rowId, idKey, childrenKey) => {
    if (!nodes?.length || rowId == null) return null;
    const childKey = childrenKey ?? 'subRows';
    for (const n of nodes) {
      if ((n[idKey] != null && String(n[idKey]) === String(rowId)) || n._tempId === rowId) return n;
      const found = findNodeInTree(n[childKey], rowId, idKey, childKey);
      if (found) return found;
    }
    return null;
  }, []);

  /** 트리 데이터에서 rowId에 해당하는 노드의 셀 값만 갱신 (값이 실제로 바뀐 경우에만 mod 'U') */
  const setCellInTree = useCallback((nodes, rowId, field, newVal, idKey, childrenKey, rowData) => {
    if (!nodes) return nodes;
    const childKey = childrenKey ?? 'subRows';
    return nodes.map((n) => {
      const match = (n[idKey] != null && String(n[idKey]) === String(rowId)) || n._tempId === rowId;
      if (match) {
        const originalValue = n[field];
        const valueChanged = originalValue !== newVal;
        const updated = { ...n, [field]: newVal };
        if (valueChanged && rowData?.mod !== 'A' && rowData?.mod !== 'D') updated.mod = 'U';
        return updated;
      }
      const sub = n[childKey];
      if (!sub?.length) return n;
      return { ...n, [childKey]: setCellInTree(sub, rowId, field, newVal, idKey, childKey, rowData) };
    });
  }, []);

  /** 트리에서 특정 노드와 그 모든 자손에 field 값 일괄 설정 (체크박스 연동용, mod는 변경하지 않음) */
  const setDescendantsField = useCallback((childNodes, field, newVal, childrenKey) => {
    if (!childNodes?.length) return childNodes;
    return childNodes.map((n) => ({
      ...n,
      [field]: newVal,
      [childrenKey]: setDescendantsField(n[childrenKey], field, newVal, childrenKey),
    }));
  }, []);
  const setTreeCheckboxCascade = useCallback((nodes, rowId, field, newVal, idKey, childrenKey) => {
    if (!nodes) return nodes;
    const childKey = childrenKey ?? 'subRows';
    return nodes.map((n) => {
      const match = (n[idKey] != null && String(n[idKey]) === String(rowId)) || n._tempId === rowId;
      if (match) {
        return { ...n, [field]: newVal, [childKey]: setDescendantsField(n[childKey], field, newVal, childKey) };
      }
      const sub = n[childKey];
      if (!sub?.length) return n;
      return { ...n, [childKey]: setTreeCheckboxCascade(sub, rowId, field, newVal, idKey, childKey) };
    });
  }, [setDescendantsField]);

  /** 트리 체크박스: 해당 노드(부모) + 모든 자손에 field 값 일괄 설정 후 setData (dataRef 기준으로 계산해 부모도 확실히 반영) */
  const directSaveTreeCheckboxCascade = useCallback((rowId, field, newVal) => {
    if (!isTree || !treeOptions) return;
    const idKey = treeOptions.idKey ?? 'id';
    const childrenKey = treeOptions.childrenKey ?? 'subRows';
    const prev = dataRef.current ?? [];
    const next = setTreeCheckboxCascade(prev, rowId, field, newVal, idKey, childrenKey);
    flushSync(() => {
      setDataRef.current?.(next);
    });
    // 화면구성 등: cascade 후에도 afterSave 훅으로 부모 동기화·저장 반영
    const updated = findNodeInTree(next, rowId, idKey, childrenKey);
    if (updated) {
      gridEvents.onAfterSaveCell?.(updated, { field, value: newVal });
    }
  }, [isTree, treeOptions, setTreeCheckboxCascade, findNodeInTree, gridEvents]);

  /** 정렬/필터/페이징 시에도 행을 식별하는 안정적 id (row.id 기반, editingCell에 사용) */
  const getRowId = useCallback(
    (originalRow, index) => {
      if (isTree && treeOptions?.idKey) {
        const idVal = originalRow?.[treeOptions.idKey];
        if (idVal !== undefined && idVal !== null && String(idVal).trim() !== '') return String(idVal);
        return originalRow?._tempId ?? originalRow?.id ?? `row-${index}`;
      }
      if (originalRow?._tempId != null && String(originalRow._tempId).trim() !== '') {
        return String(originalRow._tempId);
      }
      // 월별예산 등: 화면에서 부여한 고유 행키 우선 (fundUid/requestUid 중복 방지)
      if (originalRow?._rowUid != null && String(originalRow._rowUid).trim() !== '') {
        return String(originalRow._rowUid);
      }
      if (originalRow?.id != null && String(originalRow.id).trim() !== '') {
        return String(originalRow.id);
      }
      // 도메인 UID (재원·부서·구성 등) — index 기반 row-N 회피
      // lineKey는 복합키가 중복될 수 있어 행 id로 쓰지 않음 (월별예산 다중선택 버그 원인)
      // fundUid/fundPkgUid는 다수 행이 공유하므로 행 id로 쓰지 않음
      // requestUid도 마스터-디테일에서 디테일 N행이 공유 → docUid/detailUid 등을 반드시 앞에 둠
      const uidKeys = [
        'docUid',
        'detailUid',
        'detailId',
        'evidenceUid',
        'memberId',
        'deptUid',
        'subjectUid',
        'projectUid',
        'mapUid',
        'proposalUid',
        'obligationUid',
        'resolutionUid',
        'contractUid',
        'requestUid',
      ];
      for (const key of uidKeys) {
        const v = originalRow?.[key];
        if (v != null && String(v).trim() !== '') return String(v);
      }
      // 마스터-디테일: 디테일에 roundUid가 있어도 adjNo로 행을 구분 (동일 roundUid 충돌 방지)
      if (originalRow?.adjNo != null && originalRow?.adjNo !== '') {
        const uid = originalRow.roundUid != null ? String(originalRow.roundUid) : '';
        return uid ? `${uid}#adj-${originalRow.adjNo}` : `adj-${originalRow.adjNo}`;
      }
      if (originalRow?.roundUid != null && String(originalRow.roundUid).trim() !== '') {
        return String(originalRow.roundUid);
      }
      return `row-${index}`;
    },
    [isTree, treeOptions]
  );

  /** getRowId와 동일 규칙으로 data 배열 인덱스 조회 (저장/편집 시 row.id 매칭) */
  const findDataIndexByRowId = useCallback(
    (list, rowId) => {
      if (!list?.length || rowId == null) return -1;
      let idx = list.findIndex((r, i) => getRowId(r, i) === String(rowId) || getRowId(r, i) === rowId);
      if (idx >= 0) return idx;
      idx = list.findIndex((r) => (r._tempId ?? r.id) === rowId);
      if (idx >= 0) return idx;
      if (typeof rowId === 'string' && rowId.startsWith('row-')) {
        const indexFromId = parseInt(rowId.replace('row-', ''), 10);
        if (!Number.isNaN(indexFromId) && indexFromId >= 0 && indexFromId < list.length) return indexFromId;
      }
      return -1;
    },
    [getRowId]
  );

  /** 편집 중·인풋 포커스 중에는 가로채지 않음 (셀 저장 직후 setTimeout이 새 편집을 깨는 것 방지) */
  const focusGrid = useCallback(() => {
    setTimeout(() => {
      if (editingCellRef.current?.rowId != null) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      gridWrapRef.current?.focus();
    }, 0);
  }, []);

  /** 체크박스 등에서 편집 모드 없이 셀 값만 저장 (클릭/스페이스 즉시 토글용) */
  const directSaveCell = useCallback((rowId, field, newValue, opts = {}) => {
    const { rowData: rowDataArg, colMeta } = opts;
    const prev = dataRef.current ?? [];
    const idx = findDataIndexByRowId(prev, rowId);
    if (idx < 0 || !prev[idx]) return;
    const rowData = rowDataArg ?? prev[idx];
    const originalValue = rowData[field];
    gridEvents.onBeforeSaveCell?.(rowData, { field, value: newValue });
    // 체크박스 등 클릭 시 화면이 즉시 갱신되도록 동기적으로 상태 반영
    flushSync(() => {
      setDataRef.current?.((p) => {
        const next = [...p];
        if (!next[idx]) return next;
        next[idx] = { ...next[idx], [field]: newValue };
        const isDate = colMeta?.type === 'date';
        const valueChanged = isDate
          ? normalizeDateForCompare(originalValue) !== normalizeDateForCompare(newValue)
          : originalValue !== newValue;
        if (valueChanged && next[idx].mod !== 'A' && next[idx].mod !== 'D') next[idx].mod = 'U';
        return next;
      });
    });
    const updated = { ...prev[idx], [field]: newValue };
    gridEvents.onAfterSaveCell?.(updated, { field, value: newValue });
  }, [gridEvents, findDataIndexByRowId]);

  const table = useReactTable({ // TanStack Table 컴포넌트 생성
    data,
    columns,
    getRowId,
    getSubRows: isTree ? (row) => row[treeOptions?.childrenKey ?? 'subRows'] : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: columnFiltersEnabled ? getFilteredRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: columnFiltersEnabled ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: columnFiltersEnabled ? getFacetedUniqueValues() : undefined,
    getExpandedRowModel: isTree ? getExpandedRowModel() : undefined,
    enableColumnFilters: columnFiltersEnabled,
    enableGlobalFilter: columnFiltersEnabled,
    globalFilterFn: (row, _columnId, filterValue) => {
      if (!filterValue?.length) return true;
      return rowMatchesAllHideRules(row.original, filterValue);
    },
    state: {
      ...(isTree ? { expanded } : {}),
      columnSizing,
      columnSizingInfo,
      sorting,
      columnVisibility,
      columnOrder,
      ...(columnFiltersEnabled ? { columnFilters, globalFilter: hideRules } : {}),
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: columnFiltersEnabled ? setColumnFilters : undefined,
    onColumnOrderChange: setColumnOrder,
    onExpandedChange: isTree ? setExpanded : undefined,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    columnResizeMode: 'onEnd', // 드래그 끝날 때 너비 반영
    meta: {
      editingCell,
      focusedCell,
      stackFocus,
      setEditingCell,
      setData: (fn) => setDataRef.current?.(fn),
      onBoundaryArrow,
      onEnterMoveDown,
      onEnterMoveUp,
      onTabMove,
      onFillSelection: handleFillSelection,
      onBeforeSaveCell: gridEvents.onBeforeSaveCell,
      onAfterSaveCell: gridEvents.onAfterSaveCell,
      focusGrid,
      directSaveCell,
      colOptions,
      treeOptions: isTree ? treeOptions : undefined,
      isTree,
      setCellInTree: isTree ? setCellInTree : undefined,
      directSaveTreeCheckboxCascade: isTree ? directSaveTreeCheckboxCascade : undefined,
      isRowChecked: enableRowCheck ? isRowChecked : undefined,
      toggleRowCheck: enableRowCheck ? toggleRowCheck : undefined,
      checkAllVisibleRows: enableRowCheck ? checkAllVisibleRows : undefined,
      renderCellEditor: ({ row, column, value, meta }) => { // 셀 편집기 렌더링
        const field = column.id;
        const rowData = row.original;
        const doFocusGrid = table.options.meta?.focusGrid;
        const saveEdit = (newVal, opts) => {
          const patch =
            opts?.patch && typeof opts.patch === 'object' && !Array.isArray(opts.patch)
              ? opts.patch
              : null;
          const cellInfo = { field, value: newVal, patch: patch || undefined };
          table.options.meta?.onBeforeSaveCell?.(rowData, cellInfo);
          const rowId = row.id;
          const doTree = table.options.meta?.isTree && typeof table.options.meta?.setCellInTree === 'function';
          const treeOpts = table.options.meta?.treeOptions;
          let savedRow = rowData;
          // onAfterSaveCell / getData()가 같은 틱에서 최신값을 보도록 동기 반영
          flushSync(() => {
            setDataRef.current?.((prev) => {
              let next;
              if (doTree && treeOpts) {
                next = table.options.meta.setCellInTree(
                  prev,
                  rowId,
                  field,
                  newVal,
                  treeOpts.idKey,
                  treeOpts.childrenKey ?? 'subRows',
                  rowData
                );
                // Suggest 등 다필드 확정
                if (patch) {
                  const tIdx = findDataIndexByRowId(next, rowId);
                  if (tIdx >= 0 && next[tIdx]) {
                    next = [...next];
                    next[tIdx] = { ...next[tIdx], ...patch, [field]: newVal };
                    savedRow = next[tIdx];
                  }
                }
              } else {
                next = [...prev];
                const idx = findDataIndexByRowId(next, rowId);
                if (idx >= 0 && next[idx]) {
                  next[idx] = {
                    ...next[idx],
                    ...(patch || {}),
                    [field]: newVal,
                  };
                  const originalValue = value;
                  const isDate = meta?.type === 'date';
                  let valueChanged = isDate
                    ? normalizeDateForCompare(originalValue) !== normalizeDateForCompare(newVal)
                    : originalValue !== newVal;
                  if (!valueChanged && patch) {
                    valueChanged = Object.keys(patch).some(
                      (k) => String(rowData?.[k] ?? '') !== String(patch[k] ?? '')
                    );
                  }
                  if (valueChanged && rowData.mod !== 'A' && rowData.mod !== 'D') {
                    next[idx].mod = 'U';
                  }
                  savedRow = next[idx];
                }
              }
              dataRef.current = next;
              return next;
            });
          });
          table.options.meta?.onAfterSaveCell?.(savedRow, cellInfo);
          setEditingCell({ rowId: null, field: null, initialKey: undefined });
          if (opts?.focusGrid !== false) doFocusGrid?.();
        };
        const cancelEdit = () => {
          setEditingCell({ rowId: null, field: null, initialKey: undefined });
          doFocusGrid?.();
        };
        const type = meta?.type || 'text';

        const boundaryArrow = table.options.meta?.onBoundaryArrow;
        const enterMoveDown = table.options.meta?.onEnterMoveDown;
        const enterMoveUp = table.options.meta?.onEnterMoveUp;
        const onTabMove = table.options.meta?.onTabMove;
        const onFillSelection = table.options.meta?.onFillSelection;
        const initialKey = (type === 'text' || type === 'number') ? table.options.meta?.editingCell?.initialKey : undefined;
        const wrap = (editor) => <EditorCellWrap>{editor}</EditorCellWrap>;
        if (type === 'text') { // 텍스트 셀 편집기
          return wrap(
            <TextCellEditor
              value={value ?? ''}
              initialKey={initialKey}
              onSave={saveEdit}
              onCancel={cancelEdit}
              onBoundaryArrow={boundaryArrow}
              onEnterMoveDown={enterMoveDown}
              onEnterMoveUp={enterMoveUp}
              onTabMove={onTabMove}
              onFillSelection={onFillSelection}
              showSearch={!!meta?.showSearch}
              onSearch={meta?.onSearch}
              searchTitle={meta?.searchTitle || '코드 조회'}
              formatCalcBasis={!!meta?.formatCalcBasis}
              suggest={meta?.suggest || null}
              field={field}
              rowData={rowData}
              rowId={row.id}
            />
          );
        }
        if (type === 'number') { // 숫자 셀 편집기
          return wrap(
            <NumberCellEditor
              value={value}
              initialKey={initialKey}
              onSave={saveEdit}
              onCancel={cancelEdit}
              onBoundaryArrow={boundaryArrow}
              onEnterMoveDown={enterMoveDown}
              onEnterMoveUp={enterMoveUp}
              onTabMove={onTabMove}
              onFillSelection={onFillSelection}
            />
          );
        }
        if (type === 'date') { // 날짜 셀 편집기
          return wrap(
            <DateCellEditor
              value={value}
              onSave={saveEdit}
              onCancel={cancelEdit}
              onBoundaryArrow={boundaryArrow}
              onEnterMoveDown={enterMoveDown}
              onEnterMoveUp={enterMoveUp}
              onTabMove={onTabMove}
              onFillSelection={onFillSelection}
              meta={meta}
            />
          );
        }
        if (type === 'combo') { // 콤보 셀 편집기
          return wrap(
            <ComboCellEditor
              value={value ?? ''}
              options={meta?.options || []}
              onSave={saveEdit}
              onCancel={cancelEdit}
              onEnterMoveDown={enterMoveDown}
              onEnterMoveUp={enterMoveUp}
              onTabMove={onTabMove}
              onFillSelection={onFillSelection}
            />
          );
        }
        if (type === 'checkbox') { // 체크박스 셀 편집기 (값: 'Y'/'N')
          return wrap(
            <CheckboxCellEditor
              value={value ?? ''}
              onSave={saveEdit}
              onCancel={cancelEdit}
              onEnterMoveDown={enterMoveDown}
              onEnterMoveUp={enterMoveUp}
              onTabMove={onTabMove}
              meta={meta}
            />
          );
        }
        return wrap(
          <TextCellEditor
            value={value ?? ''}
            initialKey={initialKey}
            onSave={saveEdit}
            onCancel={cancelEdit}
            onBoundaryArrow={boundaryArrow}
            onEnterMoveDown={enterMoveDown}
            onEnterMoveUp={enterMoveUp}
            onTabMove={onTabMove}
            onFillSelection={onFillSelection}
          />
        );
      },
    },
  });

  rowsRef.current = table.getRowModel().rows;
  columnIdsRef.current = table.getHeaderGroups()[0]?.headers?.map((h) => h.column.id) ?? [];
  columnsRef.current = columns;
  tableApiRef.current = table;

  const rows = table.getRowModel().rows;

  const mergeOverlayColumns = useMemo(() => {
    const idSet = new Set(effectiveMergeIds);
    return (dataColumns || []).map((c) => {
      const field = c.id ?? c.accessorKey ?? c.field;
      return { ...c, meta: { ...(c.meta || {}), mergeRows: idSet.has(field) } };
    });
  }, [dataColumns, effectiveMergeIds]);

  const bodyMergeMap = useMemo(
    () => (hasBodyMerge ? buildBodyMergeMap(rows, mergeOverlayColumns) : null),
    [hasBodyMerge, rows, mergeOverlayColumns]
  );

  const safeFreezeRowCount = enableFreeze && !isTree && !hasBodyMerge
    ? Math.max(0, Math.min(Number(freezeRowCount) || 0, rows.length))
    : 0;
  const frozenDisplayRows = safeFreezeRowCount > 0 ? rows.slice(0, safeFreezeRowCount) : [];
  const scrollRowCount = Math.max(0, rows.length - safeFreezeRowCount);
  const scrollDisplayRows = safeFreezeRowCount > 0 ? rows.slice(safeFreezeRowCount) : rows;

  /** 가상 스크롤: 트리·본문병합 미사용이고 useVirtualRows일 때만 적용 */
  const enableVirtual = Boolean(useVirtualRows && !isTree && !hasBodyMerge && scrollRowCount > 0);
  const rowVirtualizer = useVirtualizer({
    count: scrollRowCount,
    getScrollElement: () => gridWrapRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  });
  const virtualItems = enableVirtual ? rowVirtualizer.getVirtualItems() : [];
  const totalSize = enableVirtual ? rowVirtualizer.getTotalSize() : 0;

  const columnIds = useMemo(
    () => columns.map((c) => c.id),
    [columns]
  );
  const selectedRowIndexDerived = focusedCell?.rowId != null
    ? rows.findIndex((r) => r.id === focusedCell.rowId)
    : -1;
  const selectedRowIndex = selectedRowIndexProp ?? (selectedRowIndexDerived >= 0 ? selectedRowIndexDerived : null);

  /** 포커스된 셀 변경 시(클릭/방향키/Tab/Enter 공통) onSelectRow, onSelectCell 호출 */
  useEffect(() => {
    if (focusedCell?.rowId == null || focusedCell?.field == null) return;
    const rows = rowsRef.current;
    if (!rows?.length) return;
    const rowIndex = rows.findIndex((r) => r.id === focusedCell.rowId);
    if (rowIndex < 0) return;
    const row = rows[rowIndex];
    if (!row?.original) return;
    gridEvents.onSelectRow?.(row.original, rowIndex);
    gridEvents.onSelectCell?.(row.original, { field: focusedCell.field });
  }, [focusedCell?.rowId, focusedCell?.field, gridEvents]);

  //
  const onRowClick = useCallback( // 행 클릭 이벤트 처리
    (row, rowIndex) => {
      onSelectionChange?.(rowIndex);
      gridEvents.onRowClicked?.({ data: row, rowIndex });
      gridEvents.onGridClicked?.({ data: row, rowIndex });
    },
    [onSelectionChange, gridEvents]
  );

  const pendingEditGenRef = useRef(0);

  const resolveStackHalfFromEvent = useCallback((e, column) => {
    const sp = column?.columnDef?.meta?.stackPair || column?.meta?.stackPair;
    if (!sp) return null;
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (!rect) return sp.top;
    const isBottom = e.clientY >= rect.top + rect.height / 2;
    return isBottom ? sp.bottom : sp.top;
  }, []);

  const onCellClick = useCallback( // 셀 클릭 이벤트 처리
    (e, row, column, rowIndex) => {
      e.stopPropagation(); // TR onRowClick과 충돌·이중 처리 방지
      // 더블클릭의 두 번째 click(detail>=2)은 편집 진입하지 않음 — 더블클릭 핸들러가 처리
      if (e.detail >= 2) return;
      const sp = column.columnDef?.meta?.stackPair;
      // 2단 스택: 클릭 Y로 상/하 필드 결정
      let editField = column.id;
      if (sp) {
        editField = resolveStackHalfFromEvent(e, column) || sp.top;
        setStackFocus({ rowId: row.id, field: editField });
      } else {
        setStackFocus(null);
      }
      const cellInfo = { field: editField };
      gridEvents.onCellClicked?.({ data: row.original, colDef: { field: editField }, rowIndex });
      gridEvents.onGridCellClicked?.(row.original, cellInfo, rowIndex);
      const meta = column.columnDef.meta;
      // 체크박스 컬럼: 셀 영역 클릭 시에는 아무 동작 안 함 (체크박스 클릭은 체크박스 자체 onClick에서 처리)
      if (meta?.type === 'checkbox') {
        return;
      }
      // buildTableColumns 결과와 동일 정의로 편집 가능 여부 판정
      const colDef = columns.find((c) => c.id === column.id) || column.columnDef;
      let editable = getIsEditable(colDef, row.original, colOptions);
      if (sp) {
        const halfOk = editField === sp.bottom
          ? sp.bottomEditable !== false
          : sp.topEditable !== false;
        editable = editable && halfOk;
      }
      if (editable) {
        gridEvents.onBeforeEditCell?.(row.original, cellInfo);
        const gen = ++pendingEditGenRef.current;
        // mousedown의 focusedCell·scroll 처리가 끝난 뒤 편집 진입 (포커스 레이스 방지)
        requestAnimationFrame(() => {
          if (gen !== pendingEditGenRef.current) return;
          setEditingCell({ rowId: row.id, field: editField });
        });
      }
    },
    [gridEvents, getIsEditable, colOptions, columns, resolveStackHalfFromEvent]
  );

  const onCellDoubleClick = useCallback( // 셀 더블클릭 이벤트 처리
    (e, row, column, rowIndex) => {
      e.stopPropagation();
      // 편집 가능 셀: 첫 클릭으로 열린/예약된 편집을 취소하고 더블클릭 콜백 우선
      pendingEditGenRef.current += 1;
      setEditingCell({ rowId: null, field: null, initialKey: undefined });
      const cellInfo = { field: column.id };
      gridEvents.onGridCellDoubleClicked?.(row.original, cellInfo, rowIndex);
    },
    [gridEvents]
  );

  /** 편집 모드 진입 직후 콜백 */
  useEffect(() => { // 편집 모드 진입 직후 콜백
    if (editingCell?.rowId == null || editingCell?.field == null) return;
    const rows = rowsRef.current;
    const row = rows?.find((r) => r.id === editingCell.rowId);
    if (row?.original) gridEvents.onAfterEditCell?.(row.original, { field: editingCell.field });
  }, [editingCell?.rowId, editingCell?.field, gridEvents]);

  const rowIds = rows.map((r) => r.id);

  /** 선택 영역을 탭/줄바꿈 포맷으로 클립보드에 복사 (엑셀 등에 붙여넣기 가능) */
  const handleCopySelection = useCallback(() => {
    if (!selectionRange || !rows?.length || !columnIds?.length) return;
    const rMin = Math.min(
      rowIds.indexOf(selectionRange.startRowId),
      rowIds.indexOf(selectionRange.endRowId)
    );
    const rMax = Math.max(
      rowIds.indexOf(selectionRange.startRowId),
      rowIds.indexOf(selectionRange.endRowId)
    );
    const cMin = Math.min(
      columnIds.indexOf(selectionRange.startField),
      columnIds.indexOf(selectionRange.endField)
    );
    const cMax = Math.max(
      columnIds.indexOf(selectionRange.startField),
      columnIds.indexOf(selectionRange.endField)
    );
    if (rMin < 0 || cMin < 0) return;
    const formatCell = (val) => {
      if (val == null) return '';
      if (typeof val === 'object' && val instanceof Date) return val.toISOString().slice(0, 10);
      return String(val);
    };
    const lines = [];
    for (let rIdx = rMin; rIdx <= rMax; rIdx++) {
      const row = rows[rIdx];
      if (!row?.original) continue;
      const cells = [];
      for (let cIdx = cMin; cIdx <= cMax; cIdx++) {
        const field = columnIds[cIdx];
        cells.push(formatCell(row.original[field]));
      }
      lines.push(cells.join('\t'));
    }
    const text = lines.join('\r\n');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setShowCopiedBorder(true);
        setTimeout(() => setShowCopiedBorder(false), 5000);
      }).catch(() => {});
    }
  }, [selectionRange, rows, columnIds, rowIds]);

  /** 클립보드(엑셀 등) 데이터를 그리드에 붙여넣기. editable false 또는 meta.skipPaste true 컬럼은 제외 */
  const handlePaste = useCallback(() => {
    const rows = rowsRef.current;
    const columnIds = columnIdsRef.current;
    const cols = columnsRef.current;
    if (!rows?.length || !columnIds?.length || !navigator.clipboard?.readText) return;

    const rowIds = rows.map((r) => r.id);
    let originRowIdx = 0;
    let originColIdx = 0;
    if (selectionRange) {
      originRowIdx = Math.min(
        rowIds.indexOf(selectionRange.startRowId),
        rowIds.indexOf(selectionRange.endRowId)
      );
      originColIdx = Math.min(
        columnIds.indexOf(selectionRange.startField),
        columnIds.indexOf(selectionRange.endField)
      );
    } else if (focusedCell?.rowId != null && focusedCell?.field) {
      originRowIdx = rowIds.indexOf(focusedCell.rowId);
      originColIdx = columnIds.indexOf(focusedCell.field);
    }
    if (originRowIdx < 0) originRowIdx = 0;
    if (originColIdx < 0) originColIdx = 0;

    const isPasteable = (field, rowData) => {
      const col = cols.find((c) => c.id === field);
      if (col?.meta?.skipPaste === true) return false;
      return getIsEditable(col, rowData, colOptions);
    };

    const onBefore = gridEvents.onBeforeSaveCell;
    const onAfter = gridEvents.onAfterSaveCell;

    navigator.clipboard.readText().then((text) => {
      const pastedRows = text
        .split(/\r\n|\r|\n/)
        .map((line) => line.split('\t').map((cell) => cell.trim()))
        .filter((row) => row.some((cell) => cell !== ''));
      if (!pastedRows.length) return;

      // 1칸 복사 + 블록 선택 → 선택 영역 전체에 동일 값 채우기 (엑셀과 유사)
      const isSingleCellPaste = pastedRows.length === 1 && pastedRows[0].length === 1;
      if (isSingleCellPaste && selectionRange) {
        const rMin = Math.min(
          rowIds.indexOf(selectionRange.startRowId),
          rowIds.indexOf(selectionRange.endRowId)
        );
        const rMax = Math.max(
          rowIds.indexOf(selectionRange.startRowId),
          rowIds.indexOf(selectionRange.endRowId)
        );
        const cMin = Math.min(
          columnIds.indexOf(selectionRange.startField),
          columnIds.indexOf(selectionRange.endField)
        );
        const cMax = Math.max(
          columnIds.indexOf(selectionRange.startField),
          columnIds.indexOf(selectionRange.endField)
        );
        const isMultiCell = rMin >= 0 && cMin >= 0 && (rMax > rMin || cMax > cMin);
        if (isMultiCell) {
          const raw = pastedRows[0][0];
          const value = raw === '' ? undefined : raw;
          fillSelectionWithValue(value, { allowMultiColumn: true });
          return;
        }
      }

      setDataRef.current?.((prev) => {
        const next = prev ? [...prev] : [];
        for (let r = 0; r < pastedRows.length; r++) {
          const targetRowIdx = originRowIdx + r;
          const rowData = next[targetRowIdx] || {};
          const pastedRow = pastedRows[r];
          const updates = {};
          const colIdsFromOrigin = columnIds.slice(originColIdx);
          for (let c = 0; c < colIdsFromOrigin.length && c < pastedRow.length; c++) {
            const field = colIdsFromOrigin[c];
            if (!isPasteable(field, rowData)) continue;
            const val = pastedRow[c] === '' ? undefined : pastedRow[c];
            updates[field] = val;
          }
          if (targetRowIdx < next.length) {
            const rowBefore = next[targetRowIdx];
            for (const [field, value] of Object.entries(updates)) {
              onBefore?.(rowBefore, { field, value });
            }
            next[targetRowIdx] = { ...next[targetRowIdx], ...updates };
            const row = next[targetRowIdx];
            if (row.mod !== 'A' && row.mod !== 'D') row.mod = 'U';
            for (const [field, value] of Object.entries(updates)) {
              onAfter?.(row, { field, value });
            }
          } else {
            const newRow = {
              _tempId: `__NEW_${Date.now()}_${r}`,
              mod: 'A',
              ...updates,
            };
            next.push(newRow);
            for (const [field, value] of Object.entries(updates)) {
              onBefore?.(newRow, { field, value });
              onAfter?.(newRow, { field, value });
            }
          }
        }
        return next;
      });
    }).catch(() => {});
  }, [selectionRange, focusedCell, gridEvents, getIsEditable, colOptions, fillSelectionWithValue]);

  /** 잘라내기: 복사 후 선택 영역(편집 가능 셀) 값 비우기 */
  const handleCutSelection = useCallback(() => {
    if (!selectionRange) {
      showAlert('잘라낼 영역을 선택해주세요.', 'warning');
      return;
    }
    handleCopySelection();
    const rows = rowsRef.current;
    const columnIds = columnIdsRef.current;
    const cols = columnsRef.current;
    if (!rows?.length || !columnIds?.length) return;
    const rowIds = rows.map((r) => r.id);
    const rMin = Math.min(rowIds.indexOf(selectionRange.startRowId), rowIds.indexOf(selectionRange.endRowId));
    const rMax = Math.max(rowIds.indexOf(selectionRange.startRowId), rowIds.indexOf(selectionRange.endRowId));
    const cMin = Math.min(columnIds.indexOf(selectionRange.startField), columnIds.indexOf(selectionRange.endField));
    const cMax = Math.max(columnIds.indexOf(selectionRange.startField), columnIds.indexOf(selectionRange.endField));
    if (rMin < 0 || cMin < 0) return;

    const isPasteable = (field, rowData) => {
      const col = cols?.find((c) => c.id === field);
      if (col?.meta?.skipPaste === true) return false;
      return getIsEditable(col, rowData, colOptions);
    };

    const onBefore = gridEvents.onBeforeSaveCell;
    const onAfter = gridEvents.onAfterSaveCell;
    setDataRef.current?.((prev) => {
      const next = prev ? [...prev] : [];
      for (let rIdx = rMin; rIdx <= rMax; rIdx++) {
        const displayRow = rows[rIdx];
        if (!displayRow?.original) continue;
        const dataIdx = next.findIndex(
          (r) =>
            (displayRow.original._tempId != null && r._tempId === displayRow.original._tempId) ||
            r === displayRow.original ||
            (displayRow.original.id != null && r.id === displayRow.original.id)
        );
        const targetIdx = dataIdx >= 0 ? dataIdx : rIdx;
        if (!next[targetIdx]) continue;
        for (let cIdx = cMin; cIdx <= cMax; cIdx++) {
          const field = columnIds[cIdx];
          if (!isPasteable(field, next[targetIdx])) continue;
          onBefore?.(next[targetIdx], { field, value: undefined });
          next[targetIdx] = { ...next[targetIdx], [field]: undefined };
          const row = next[targetIdx];
          if (row.mod !== 'A' && row.mod !== 'D') row.mod = 'U';
          onAfter?.(row, { field, value: undefined });
        }
      }
      return next;
    });
  }, [selectionRange, handleCopySelection, gridEvents, getIsEditable, colOptions]);

  const dataColumnIds = useMemo(
    () => (columnIds || []).filter((id) => !isHiGridSystemColumnId(id)),
    [columnIds]
  );

  const findColumnOptions = useMemo(
    () =>
      dataColumns
        .filter((c) => c.visible !== false && c.meta?.visible !== false)
        .map((c) => ({
          id: c.id ?? c.accessorKey ?? c.field,
          label: c.header ?? c.id ?? c.accessorKey,
        }))
        .filter((c) => c.id),
    [dataColumns]
  );

  const cellTextMatches = useCallback((value, findText, matchCase) => {
    if (findText == null || String(findText) === '') return false;
    const s = value == null ? '' : String(value);
    const f = String(findText);
    if (matchCase) return s.includes(f);
    return s.toLowerCase().includes(f.toLowerCase());
  }, []);

  const replaceInText = useCallback((value, findText, replaceText, matchCase) => {
    const s = value == null ? '' : String(value);
    const f = String(findText ?? '');
    const rep = replaceText == null ? '' : String(replaceText);
    if (!f) return s;
    if (matchCase) return s.split(f).join(rep);
    const re = new RegExp(f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    return s.replace(re, rep);
  }, []);

  const getActiveSelectionRange = useCallback((opts) => {
    if (opts?.scope === 'selection') {
      return findReplaceSelectionRef.current || selectionRange;
    }
    return selectionRange;
  }, [selectionRange]);

  const getFindFields = useCallback(
    (opts) => {
      if (opts.scope === 'all') return dataColumnIds;
      if (opts.scope === 'selection') {
        const range = getActiveSelectionRange(opts);
        if (range) {
          const cMin = Math.min(
            columnIds.indexOf(range.startField),
            columnIds.indexOf(range.endField)
          );
          const cMax = Math.max(
            columnIds.indexOf(range.startField),
            columnIds.indexOf(range.endField)
          );
          if (cMin >= 0) {
            return columnIds.slice(cMin, cMax + 1).filter((id) => !isHiGridSystemColumnId(id));
          }
        }
        return [];
      }
      const f = opts.field || focusedCell?.field;
      return f && !isHiGridSystemColumnId(f) ? [f] : dataColumnIds.slice(0, 1);
    },
    [dataColumnIds, columnIds, getActiveSelectionRange, focusedCell]
  );

  const isRowInFindScope = useCallback(
    (rowId, opts) => {
      if (opts.scope !== 'selection') return true;
      const range = getActiveSelectionRange(opts);
      if (!range) return false;
      const rowIds = rowsRef.current.map((r) => r.id);
      const ri = rowIds.indexOf(rowId);
      const rMin = Math.min(rowIds.indexOf(range.startRowId), rowIds.indexOf(range.endRowId));
      const rMax = Math.max(rowIds.indexOf(range.startRowId), rowIds.indexOf(range.endRowId));
      return ri >= rMin && ri <= rMax;
    },
    [getActiveSelectionRange]
  );

  const isFieldInFindScope = useCallback(
    (field, opts) => {
      if (opts.scope !== 'selection') return true;
      const range = getActiveSelectionRange(opts);
      if (!range) return false;
      const cMin = Math.min(columnIds.indexOf(range.startField), columnIds.indexOf(range.endField));
      const cMax = Math.max(columnIds.indexOf(range.startField), columnIds.indexOf(range.endField));
      const ci = columnIds.indexOf(field);
      return ci >= cMin && ci <= cMax;
    },
    [getActiveSelectionRange, columnIds]
  );

  const openFindReplaceDialog = useCallback(() => {
    findCursorRef.current = { rowIdx: -1, colIdx: -1 };
    const snap = selectionRange
      ? { ...selectionRange }
      : focusedCell?.rowId != null && focusedCell?.field
        ? {
            startRowId: focusedCell.rowId,
            endRowId: focusedCell.rowId,
            startField: focusedCell.field,
            endField: focusedCell.field,
          }
        : null;
    findReplaceSelectionRef.current = snap;

    const isMulti =
      snap &&
      (snap.startRowId !== snap.endRowId || snap.startField !== snap.endField);

    setFindReplaceMeta({
      initialScope: isMulti ? 'selection' : 'column',
      hasSelectionSnapshot: Boolean(isMulti),
    });
    // 대화상자 연 뒤에도 블록 하이라이트가 보이도록 유지
    if (isMulti && snap) setSelectionRange(snap);
    setFindReplaceOpen(true);
  }, [selectionRange, focusedCell, setSelectionRange]);

  const closeFindReplaceDialog = useCallback(() => {
    setFindReplaceOpen(false);
    // 선택 영역 스냅샷 범위 하이라이트 복원
    const snap = findReplaceSelectionRef.current;
    if (snap && (snap.startRowId !== snap.endRowId || snap.startField !== snap.endField)) {
      setSelectionRange(snap);
    }
  }, [setSelectionRange]);

  const handleFindNext = useCallback(
    (opts) => {
      const findText = opts?.findText ?? '';
      if (!String(findText)) {
        showAlert('찾을 내용을 입력하세요.', 'warning');
        return;
      }
      if (opts.scope === 'selection' && !getActiveSelectionRange(opts)) {
        showAlert('선택 영역이 없습니다. 블록을 지정한 뒤 다시 열어주세요.', 'warning');
        return;
      }
      const fields = getFindFields(opts);
      const rows = rowsRef.current ?? [];
      if (!rows.length || !fields.length) {
        showAlert('검색할 데이터가 없습니다.', 'warning');
        return;
      }

      let startRow = findCursorRef.current.rowIdx;
      let startCol = findCursorRef.current.colIdx + 1;
      if (startRow < 0) {
        const focusIdx = focusedCell?.rowId != null ? rows.findIndex((r) => r.id === focusedCell.rowId) : 0;
        startRow = Math.max(0, focusIdx);
        const fIdx = fields.indexOf(focusedCell?.field);
        startCol = fIdx >= 0 ? fIdx : 0;
      }

      const tryMatch = (rIdx, cIdx) => {
        const row = rows[rIdx];
        if (!row?.original || !isRowInFindScope(row.id, opts)) return false;
        const field = fields[cIdx];
        if (!isFieldInFindScope(field, opts)) return false;
        if (!cellTextMatches(row.original[field], findText, opts.matchCase)) return false;
        findCursorRef.current = { rowIdx: rIdx, colIdx: cIdx };
        setFocusedCell({ rowId: row.id, field });
        // 선택 영역 모드: 원본 블록 하이라이트 유지 (스냅샷 복원)
        if (opts.scope === 'selection' && findReplaceSelectionRef.current) {
          setSelectionRange({ ...findReplaceSelectionRef.current });
        } else {
          setSelectionRange({
            startRowId: row.id,
            endRowId: row.id,
            startField: field,
            endField: field,
          });
        }
        return true;
      };

      for (let rIdx = startRow; rIdx < rows.length; rIdx++) {
        const cStart = rIdx === startRow ? startCol : 0;
        for (let cIdx = cStart; cIdx < fields.length; cIdx++) {
          if (tryMatch(rIdx, cIdx)) return;
        }
      }
      for (let rIdx = 0; rIdx <= startRow; rIdx++) {
        const cEnd = rIdx === startRow ? Math.min(startCol, fields.length) : fields.length;
        for (let cIdx = 0; cIdx < cEnd; cIdx++) {
          if (tryMatch(rIdx, cIdx)) return;
        }
      }
      showAlert('더 이상 찾는 내용이 없습니다.', 'info');
    },
    [getFindFields, getActiveSelectionRange, focusedCell, cellTextMatches, isRowInFindScope, isFieldInFindScope, setFocusedCell, setSelectionRange]
  );

  const handleReplaceOne = useCallback(
    (opts) => {
      if (!allowReplace) return;
      const findText = opts?.findText ?? '';
      if (!String(findText)) {
        showAlert('찾을 내용을 입력하세요.', 'warning');
        return;
      }
      const rows = rowsRef.current ?? [];
      const row = focusedCell?.rowId != null ? rows.find((r) => r.id === focusedCell.rowId) : null;
      const field = focusedCell?.field;
      if (!row?.original || !field || isHiGridSystemColumnId(field)) {
        handleFindNext(opts);
        return;
      }
      if (!cellTextMatches(row.original[field], findText, opts.matchCase)) {
        handleFindNext(opts);
        return;
      }
      const col = columnsRef.current?.find((c) => c.id === field);
      if (!getIsEditable(col, row.original, colOptions) || col?.meta?.skipPaste === true) {
        handleFindNext(opts);
        return;
      }
      const newVal = replaceInText(row.original[field], findText, opts.replaceText, opts.matchCase);
      directSaveCell(row.id, field, newVal === '' ? undefined : newVal, {
        rowData: row.original,
        colMeta: col?.meta,
      });
      handleFindNext(opts);
    },
    [allowReplace, focusedCell, cellTextMatches, handleFindNext, getIsEditable, colOptions, replaceInText, directSaveCell]
  );

  const handleReplaceAll = useCallback(
    async (opts) => {
      if (!allowReplace) return;
      const findText = opts?.findText ?? '';
      if (!String(findText)) {
        showAlert('찾을 내용을 입력하세요.', 'warning');
        return;
      }
      if (opts.scope === 'selection' && !getActiveSelectionRange(opts)) {
        showAlert('선택 영역이 없습니다. 블록을 지정한 뒤 다시 열어주세요.', 'warning');
        return;
      }
      const ok = await showConfirm('범위 내 일치하는 내용을 모두 바꾸시겠습니까?', 'warning');
      if (!ok) return;

      const fields = getFindFields(opts);
      const rows = rowsRef.current ?? [];
      const cols = columnsRef.current;
      let count = 0;

      setDataRef.current?.((prev) => {
        const next = prev ? [...prev] : [];
        rows.forEach((displayRow) => {
          if (!displayRow?.original || !isRowInFindScope(displayRow.id, opts)) return;
          const dataIdx = next.findIndex(
            (r) =>
              (displayRow.original._tempId != null && r._tempId === displayRow.original._tempId) ||
              r === displayRow.original ||
              (displayRow.original.id != null && r.id === displayRow.original.id)
          );
          if (dataIdx < 0) return;
          let row = next[dataIdx];
          let changed = false;
          fields.forEach((field) => {
            if (!isFieldInFindScope(field, opts)) return;
            const col = cols?.find((c) => c.id === field);
            if (!getIsEditable(col, row, colOptions) || col?.meta?.skipPaste === true) return;
            if (!cellTextMatches(row[field], findText, opts.matchCase)) return;
            const newVal = replaceInText(row[field], findText, opts.replaceText, opts.matchCase);
            row = { ...row, [field]: newVal === '' ? undefined : newVal };
            changed = true;
            count += 1;
          });
          if (changed) {
            if (row.mod !== 'A' && row.mod !== 'D') row.mod = 'U';
            next[dataIdx] = row;
          }
        });
        return next;
      });

      // 블록 하이라이트 유지
      if (opts.scope === 'selection' && findReplaceSelectionRef.current) {
        setSelectionRange({ ...findReplaceSelectionRef.current });
      }

      showAlert(count > 0 ? `${count}건을 바꿨습니다.` : '바꿀 내용이 없습니다.', count > 0 ? 'success' : 'info');
    },
    [allowReplace, getFindFields, getActiveSelectionRange, isRowInFindScope, isFieldInFindScope, getIsEditable, colOptions, cellTextMatches, replaceInText, setSelectionRange]
  );

  const openContextMenuAt = useCallback(
    (e, rowId, field) => {
      const allowBodyMenu =
        enableContextMenu ||
        enableSelectionStats ||
        enableQuickExport ||
        (enableFreeze && !isTree);
      if (!allowBodyMenu) return;
      e.preventDefault();
      e.stopPropagation();

      const rowIds = rowsRef.current.map((r) => r.id);
      const colIds = columnIdsRef.current;
      const inSelection =
        selectionRange &&
        rowId != null &&
        field != null &&
        isCellInSelection(rowId, field, rowIds, colIds);
      const isMulti =
        selectionRange &&
        (selectionRange.startRowId !== selectionRange.endRowId ||
          selectionRange.startField !== selectionRange.endField);

      // 다중 블록이 있고 그 안을 우클릭한 경우: 블록 유지, 포커스만 이동
      if (rowId != null && field != null) {
        setFocusedCell({ rowId, field });
        if (!inSelection && !isMulti) {
          setSelectionRange({
            startRowId: rowId,
            endRowId: rowId,
            startField: field,
            endField: field,
          });
        } else if (!inSelection && isMulti) {
          // 블록 밖 우클릭이어도 집계용 블록은 유지 (선택만 유지)
        } else if (!selectionRange) {
          setSelectionRange({
            startRowId: rowId,
            endRowId: rowId,
            startField: field,
            endField: field,
          });
        }
      }
      setContextMenu({ x: e.clientX, y: e.clientY });
    },
    [enableContextMenu, enableSelectionStats, enableQuickExport, enableFreeze, isTree, selectionRange, isCellInSelection, setFocusedCell, setSelectionRange]
  );

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const handleQuickExport = useCallback(
    (format) => {
      // 조회·필터 반영된 화면 전체 행 (선택 블록만 아님)
      const exportRows = (rowsRef.current || []).map((r) => r.original).filter(Boolean);
      if (!exportRows.length) {
        showAlert('내보낼 데이터가 없습니다.', 'warning');
        return;
      }
      const name = exportFileName || gridId || 'grid_export';
      try {
        if (format === 'csv') downloadGridCsv(exportRows, dataColumns, name);
        else downloadGridXlsx(exportRows, dataColumns, name);
      } catch (e) {
        showAlert(e?.message || '내보내기에 실패했습니다.', 'error');
      }
    },
    [exportFileName, gridId, dataColumns]
  );

  const handleContextMenuSelect = useCallback(
    async (id) => {
      const menuPos = contextMenu;
      closeContextMenu();
      if (id === 'pivot') {
        setPivotOpen(true);
        return;
      }
      if (id === 'mergeCells') {
        setMergePopup({ x: menuPos?.x || 120, y: menuPos?.y || 80 });
        return;
      }
      if (id === 'export-xlsx') {
        handleQuickExport('xlsx');
        return;
      }
      if (id === 'export-csv') {
        handleQuickExport('csv');
        return;
      }
      if (id === 'addAbove') {
        if (onContextAddAbove) await onContextAddAbove();
        else showAlert('행 추가 핸들러가 연결되지 않았습니다.', 'warning');
        return;
      }
      if (id === 'addBelow') {
        if (onContextAddBelow) await onContextAddBelow();
        else showAlert('행 추가 핸들러가 연결되지 않았습니다.', 'warning');
        return;
      }
      if (id === 'delete') {
        if (onContextDelete) await onContextDelete();
        else showAlert('행 삭제 핸들러가 연결되지 않았습니다.', 'warning');
        return;
      }
      if (id === 'duplicate') {
        if (onContextDuplicate) await onContextDuplicate();
        else showAlert('행 복제 핸들러가 연결되지 않았습니다.', 'warning');
        return;
      }
      if (id === 'cut') {
        handleCutSelection();
        return;
      }
      if (id === 'findReplace') {
        openFindReplaceDialog();
        return;
      }
      if (id === 'freezeCol') {
        if (focusedCell?.field && !isHiGridSystemColumnId(focusedCell.field)) {
          setFreezeToColumnId(focusedCell.field);
        }
        return;
      }
      if (id === 'unfreezeCol') {
        setFreezeToColumnId(null);
        return;
      }
      if (id === 'freezeRow') {
        const idx = rowsRef.current.findIndex((r) => r.id === focusedCell?.rowId);
        if (idx >= 0) setFreezeRowCount(idx + 1);
        return;
      }
      if (id === 'unfreezeRow') {
        setFreezeRowCount(0);
      }
    },
    [
      closeContextMenu,
      handleQuickExport,
      onContextAddAbove,
      onContextAddBelow,
      onContextDelete,
      onContextDuplicate,
      handleCutSelection,
      openFindReplaceDialog,
      focusedCell,
      contextMenu,
    ]
  );

  const selectionStats = useMemo(() => {
    if (!enableSelectionStats || !selectionRange) return null;
    return computeSelectionStats({
      selectionRange,
      rows,
      columnIds,
      columns,
    });
  }, [enableSelectionStats, selectionRange, rows, columnIds, columns]);

  const contextMenuItems = useMemo(() => {
    const items = [];
    if (selectionStats) {
      items.push({
        id: 'stat-count',
        label: `데이터 건수: ${selectionStats.count.toLocaleString('ko-KR')}`,
        info: true,
      });
      if (selectionStats.hasNumeric) {
        items.push(
          {
            id: 'stat-sum',
            label: `합계: ${formatStatNumber(selectionStats.sum)}`,
            info: true,
          },
          {
            id: 'stat-avg',
            label: `평균: ${formatStatNumber(selectionStats.avg)}`,
            info: true,
          },
          {
            id: 'stat-min',
            label: `최소: ${formatStatNumber(selectionStats.min)}`,
            info: true,
          },
          {
            id: 'stat-max',
            label: `최대: ${formatStatNumber(selectionStats.max)}`,
            info: true,
          }
        );
      }
      items.push({ separator: true });
    }
    if (enableRowEditMenu && !isTree) {
      items.push(
        { id: 'addAbove', label: '위에 행 추가' },
        { id: 'addBelow', label: '아래에 행 추가' },
        { id: 'duplicate', label: '행 복제' },
        { id: 'delete', label: '행 삭제', danger: true },
        { separator: true }
      );
    }
    if (enableCut) {
      items.push({ id: 'cut', label: '잘라내기', shortcut: 'Ctrl+X' });
    }
    if (enableFindReplace) {
      items.push({ id: 'findReplace', label: allowReplace ? '찾기/바꾸기…' : '찾기…', shortcut: 'Ctrl+H' });
    }
    if (enableQuickExport) {
      items.push(
        { separator: true },
        {
          id: 'export',
          label: '내보내기',
          children: [
            { id: 'export-xlsx', label: 'Excel (.xlsx)' },
            { id: 'export-csv', label: 'CSV (.csv)' },
          ],
        }
      );
    }
    if (enablePivot) {
      items.push({ separator: true }, { id: 'pivot', label: '피벗…' });
    }
    if (enableUserMerge) {
      items.push({ id: 'mergeCells', label: '동일 값 병합…' });
    }
    if (enableFreeze && !isTree) {
      items.push(
        { separator: true },
        { id: 'freezeCol', label: '여기까지 열 고정', disabled: !focusedCell?.field || isHiGridSystemColumnId(focusedCell.field) },
        { id: 'unfreezeCol', label: '열 고정 해제', disabled: !freezeToColumnId },
        { id: 'freezeRow', label: '여기까지 행 고정', disabled: focusedCell?.rowId == null },
        { id: 'unfreezeRow', label: freezeRowCount > 0 ? `행 고정 해제 (${freezeRowCount}행)` : '행 고정 해제', disabled: !freezeRowCount }
      );
    }
    return items;
  }, [
    selectionStats,
    enableRowEditMenu,
    isTree,
    enableCut,
    enableFindReplace,
    allowReplace,
    enableQuickExport,
    enableFreeze,
    enablePivot,
    enableUserMerge,
    focusedCell,
    freezeToColumnId,
    freezeRowCount,
  ]);

  /** 방향키/Enter/Tab으로 셀 이동 시 해당 셀이 화면 밖이면 스크롤하여 보이게 함 */
  const activeCell = editingCell?.rowId != null
    ? { rowId: editingCell.rowId, field: editingCell.field }
    : (focusedCell?.rowId != null ? { rowId: focusedCell.rowId, field: focusedCell.field } : null);

  /** 편집 중 필드가 스택 상/하 중 하나여도 해당 열을 활성으로 표시 */
  const isStackFieldActive = useCallback((column, field) => {
    if (field == null) return false;
    if (column.id === field) return true;
    const sp = column.columnDef?.meta?.stackPair;
    if (!sp) return false;
    return sp.top === field || sp.bottom === field;
  }, []);

  useEffect(() => {
    if (!activeCell?.rowId) return;
    // 편집 중 smooth/instant 스크롤은 인라인 에디터 포커스를 뺏어 편집이 깜빡이다 닫힘
    if (editingCellRef.current?.rowId != null) return;
    const rows = rowsRef.current ?? [];
    const rowIndex = rows.findIndex((r) => r.id === activeCell.rowId);
    const wrap = gridWrapRef.current;
    // 맨 위 행으로 이동 시 스크롤을 끝까지 올림 (scrollIntoView만 쓰면 한 칸 남는 현상 방지)
    if (rowIndex === 0 && wrap) {
      wrap.scrollTop = 0;
    } else {
      activeCellRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }
  }, [activeCell?.rowId, activeCell?.field]);

  /** 선택 영역이 바뀌면 복사 점선 테두리 해제 */
  useEffect(() => {
    setShowCopiedBorder(false);
  }, [selectionRange]);

  const onWrapKeyDown = useCallback(
    (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;

      if ((e.key === 'F2' || e.key === ' ') && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        if (focusedCell?.rowId != null && focusedCell?.field) {
          const colDef = columns.find((c) => c.id === focusedCell.field);
          // 체크박스 컬럼: 편집 모드 없이 스페이스로 즉시 토글
          if (colDef?.meta?.type === 'checkbox') {
            const row = rows.find((r) => r.id === focusedCell.rowId);
            if (row?.original) {
              if (!getIsEditable(colDef, row.original, colOptions)) return;
              const trueVal = colDef.meta.trueValue ?? 'Y';
              const falseVal = colDef.meta.falseValue ?? 'N';
              const current = row.original[focusedCell.field];
              const newVal = current === trueVal ? falseVal : trueVal;
              directSaveCell(focusedCell.rowId, focusedCell.field, newVal, {
                rowData: row.original,
                colMeta: colDef.meta,
              });
            }
            return;
          }
          const row = rows.find((r) => r.id === focusedCell.rowId);
          const editable = row?.original && getIsEditable(colDef, row.original, colOptions);
          if (editable && row?.original) {
            gridEvents.onBeforeEditCell?.(row.original, { field: focusedCell.field });
            setEditingCell({ rowId: focusedCell.rowId, field: focusedCell.field, initialKey: undefined });
          }
        }
        return;
      }

      if (e.key === 'Enter') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          if (selectionRange && focusedCell?.rowId != null && focusedCell?.field != null) {
            const rowIdx = rows.findIndex((r) => r.id === focusedCell.rowId);
            const row = rowIdx >= 0 ? rows[rowIdx] : null;
            const fillValue = row?.original?.[focusedCell.field];
            handleFillSelection(fillValue);
          }
          return;
        }
        e.preventDefault();
        // Enter = 아래 행, Shift+Enter = 위 행 (동일 컬럼)
        moveFocusedCellVertical(
          e.shiftKey ? 'up' : 'down',
          rows,
          columnIds,
          onSelectionChange
        );
        return;
      }

      if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
        if (selectionRange) {
          handleCopySelection();
          e.preventDefault();
        }
        return;
      }

      if (e.key === 'x' && (e.ctrlKey || e.metaKey)) {
        if (enableContextMenu && enableCut && selectionRange) {
          e.preventDefault();
          handleCutSelection();
        }
        return;
      }

      if (e.key === 'h' && (e.ctrlKey || e.metaKey)) {
        if (enableContextMenu && enableFindReplace) {
          e.preventDefault();
          openFindReplaceDialog();
        }
        return;
      }

      if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
        handlePaste();
        e.preventDefault();
        return;
      }

      /** 키보드로 편집 진입: 단일 문자 입력 시 e.key를 initialKey로 전달. 한글 등 IME 조합 중에는 e.key가 'Process'이므로 제외 → 빈 상태로 열림 */
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && focusedCell?.rowId != null && focusedCell?.field) {
        const colDef = columns.find((c) => c.id === focusedCell.field);
        const row = rows.find((r) => r.id === focusedCell.rowId);
        const editable = row?.original && getIsEditable(colDef, row.original, colOptions);
        if (editable && row?.original) {
          e.preventDefault();
          gridEvents.onBeforeEditCell?.(row.original, { field: focusedCell.field });
          const isIme = e.isComposing || e.key === 'Process' || e.key === 'Unidentified';
          const initialKey = isIme ? '' : e.key;
          setEditingCell({ rowId: focusedCell.rowId, field: focusedCell.field, initialKey });
          return;
        }
      }

      handleFocusKeyDown(e, rows, columnIds, onSelectionChange);
    },
    [handleFocusKeyDown, moveFocusedCellVertical, rows, columnIds, onSelectionChange, focusedCell, columns, selectionRange, handleCopySelection, handlePaste, handleFillSelection, handleCutSelection, openFindReplaceDialog, gridEvents, directSaveCell, getIsEditable, colOptions, enableContextMenu, enableCut, enableFindReplace]
  );



  
  // gridRef에 gridControl Methods 정의
  // gridControl Methods 는 HOOK을 통하여 호출 가능한 인스턴스처럼 사용 가능함
  useImperativeHandle(
    ref,
    () => ({
      // ---- Data ----
      getData: () => dataRef.current ?? [],
      /** 현재 화면(필터·정렬)에 보이는 행 데이터 */
      getFilteredData: () => {
        const api = tableApiRef.current;
        if (!api?.getFilteredRowModel) return dataRef.current ?? [];
        return api.getFilteredRowModel().rows.map((r) => r.original);
      },
      /** 엑셀 등 내보내기용: rowNumber/mod 제외한 데이터 컬럼 정의 배열 (header, accessorKey/id) */
      getDataColumns: () => dataColumns ?? [],
      setData: (fnOrValue) => {
        // getData()가 같은 틱에서 최신값을 보도록 dataRef 동기 반영
        setData((prev) => {
          const next = typeof fnOrValue === 'function' ? fnOrValue(prev) : fnOrValue;
          dataRef.current = next ?? [];
          return next;
        });
      },
      clearColumnFilters: () => setColumnFilters([]),
      // ---- Selection ----
      getSelectedRowIndex: () => {
        const r = rowsRef.current;
        const fc = focusedCell;
        if (!fc?.rowId || !r?.length) return null;
        const idx = r.findIndex((row) => row.id === fc.rowId);
        return idx >= 0 ? idx : null;
      },
      setSelectedRowIndex: (idx) => {
        const r = rowsRef.current;
        const cols = columnIdsRef.current;
        if (!r?.length || !cols?.length) return;
        const row = r[idx];
        if (row) setFocusedCell({ rowId: row.id, field: cols[0] });
      },
      getSelectedRow: () => {
        const r = rowsRef.current;
        const idx = r?.length ? r.findIndex((row) => row.id === focusedCell?.rowId) : -1;
        if (idx < 0) return null;
        return r[idx]?.original ?? null;
      },
      getSelectedRowId: () => focusedCell?.rowId ?? null,
      setSelectedRowById: (rowId) => {
        const r = rowsRef.current;
        const cols = columnIdsRef.current;
        if (!r?.length || !cols?.length || rowId == null) return;
        const row = r.find((x) => x.id === rowId);
        if (row) setFocusedCell({ rowId: row.id, field: cols[0] });
      },
      getRowById: (rowId) => {
        const data = dataRef.current ?? [];
        if (rowId == null) return null;
        if (isTree && treeOptions) {
          const idKey = treeOptions.idKey ?? 'id';
          const childKey = treeOptions.childrenKey ?? 'subRows';
          return findNodeInTree(data, rowId, idKey, childKey) ?? null;
        }
        if (typeof rowId === 'string' && rowId.startsWith('row-')) {
          const idx = parseInt(rowId.replace('row-', ''), 10);
          if (!Number.isNaN(idx) && idx >= 0 && idx < data.length) return data[idx] ?? null;
        }
        const idx = findDataIndexByRowId(data, rowId);
        return idx >= 0 ? data[idx] ?? null : null;
      },
      setCellValueById: (rowId, field, value) => {
        const data = dataRef.current ?? [];
        if (rowId == null || field == null) return;
        if (isTree && treeOptions) {
          const idKey = treeOptions.idKey ?? 'id';
          const childKey = treeOptions.childrenKey ?? 'subRows';
          const row = findNodeInTree(data, rowId, idKey, childKey);
          if (!row) return;
          gridEvents.onBeforeSaveCell?.(row, { field, value });
          setDataRef.current?.((prev) => setCellInTree(prev, rowId, field, value, idKey, childKey, row));
          const updatedRow = { ...row, [field]: value };
          if (updatedRow.mod !== 'A' && updatedRow.mod !== 'D') updatedRow.mod = 'U';
          gridEvents.onAfterSaveCell?.(updatedRow, { field, value });
          return;
        }
        const rowIndex = findDataIndexByRowId(data, rowId);
        if (rowIndex < 0) return;
        const row = data[rowIndex];
        if (!row) return;
        const updatedRow = { ...row, [field]: value };
        if (updatedRow.mod !== 'A' && updatedRow.mod !== 'D') updatedRow.mod = 'U';
        gridEvents.onBeforeSaveCell?.(row, { field, value });
        setDataRef.current?.((prev) => {
          const next = [...prev];
          if (!next[rowIndex]) return prev;
          next[rowIndex] = { ...updatedRow };
          return next;
        });
        gridEvents.onAfterSaveCell?.(updatedRow, { field, value });
      },
      clearSelection: () => {
        setFocusedCell({ rowId: null, field: null });
      },
      /** 필수값 검증. 트리 모드면 표시 중인 모든 행(루트+자식) 검사, 일반 그리드는 루트만 */
      requiredValid: () => {
        const fieldsToCheck = (dataColumns || [])
          .filter((c) => c.meta?.required === true)
          .map((c) => c.accessorKey || c.id)
          .filter(Boolean);
        if (fieldsToCheck.length === 0) return true;

        if (isTree && treeOptions) {
          const visibleRows = rowsRef.current ?? [];
          for (let i = 0; i < visibleRows.length; i++) {
            const tableRow = visibleRows[i];
            const rowData = tableRow?.original ?? tableRow;
            for (const field of fieldsToCheck) {
              const val = rowData?.[field];
              if (val === undefined || val === null || val === '') {
                const headerLabel = columns.find((c) => c.id === field)?.header ?? field;
                showAlert(`${i + 1}번째 행의 [${headerLabel}] 항목을 입력해주세요.`, 'warning');
                if (tableRow?.id != null) {
                  setFocusedCell({ rowId: tableRow.id, field });
                  setEditingCell({ rowId: tableRow.id, field });
                }
                return false;
              }
            }
          }
          return true;
        }

        const data = dataRef.current ?? [];
        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          for (const field of fieldsToCheck) {
            const val = row?.[field];
            if (val === undefined || val === null || val === '') {
              showAlert(`${i + 1} 행의 [${columns.find((c) => c.id === field)?.header}] 항목을 입력해주세요.`, 'warning');
              const r = rowsRef.current;
              const cols = columnIdsRef.current;
              if (r?.[i] && cols?.length) {
                setFocusedCell({ rowId: r[i].id, field });
                setEditingCell({ rowId: r[i].id, field });
              }
              return false;
            }
          }
        }
        return true;
      },
      // ---- Cell ----
      getCellValue: (rowIndex, field) => {
        const data = dataRef.current ?? [];
        const row = data[rowIndex];
        return row != null && field != null ? row[field] : undefined;
      },
      setCellValue: (rowIndex, field, value) => {
        const data = dataRef.current ?? [];
        if (rowIndex < 0 || rowIndex >= data.length || field == null) return;
        const row = data[rowIndex];
        if (!row) return;
        gridEvents.onBeforeSaveCell?.(row, { field, value });
        pendingSetCellValueQueueRef.current.push({ rowIndex, field, value });
        if (!setCellValueFlushScheduledRef.current) {
          setCellValueFlushScheduledRef.current = true;
          queueMicrotask(() => {
            setCellValueFlushScheduledRef.current = false;
            const queue = pendingSetCellValueQueueRef.current.slice();
            pendingSetCellValueQueueRef.current = [];
            if (queue.length === 0) return;
            const ge = ref?.gridEventsRef?.current ?? {};
            setDataRef.current?.((prev) => {
              const next = prev ? [...prev] : [];
              for (const { rowIndex: ri, field: f, value: v } of queue) {
                if (ri < 0 || ri >= next.length) continue;
                const r = next[ri];
                if (!r) continue;
                const updatedRow = { ...r, [f]: v };
                if (updatedRow.mod !== 'A' && updatedRow.mod !== 'D') updatedRow.mod = 'U';
                next[ri] = updatedRow;
                ge.onAfterSaveCell?.(updatedRow, { field: f, value: v });
              }
              return next;
            });
          });
        }
      },
      /** 한 번의 setData로 여러 셀 값 일괄 반영 (연속 setCellValue 시 배칭 이슈 방지) */
      setCellValues: (rowIndex, values) => {
        if (rowIndex < 0 || values == null || typeof values !== 'object') return;
        setDataRef.current?.((prev) => {
          if (!prev?.length || rowIndex >= prev.length) return prev;
          const next = [...prev];
          const row = next[rowIndex];
          if (!row) return prev;
          const updatedRow = { ...row, ...values };
          if (updatedRow.mod !== 'A' && updatedRow.mod !== 'D') updatedRow.mod = 'U';
          next[rowIndex] = updatedRow;
          Object.entries(values).forEach(([field, value]) => {
            gridEvents.onBeforeSaveCell?.(row, { field, value });
            gridEvents.onAfterSaveCell?.(updatedRow, { field, value });
          });
          return next;
        });
      },
      // ---- Row ----
      getRowCount: () => (dataRef.current ?? []).length,
      getRow: (rowIndex) => {
        const data = dataRef.current ?? [];
        return data[rowIndex] ?? null;
      },
      // ---- Focus/Edit ----
      focusGrid: () => focusGrid(),
      startEditCell: (rowIndex, field) => {
        const r = rowsRef.current;
        const cols = columnsRef.current;
        const idx = typeof rowIndex === 'number' && !Number.isNaN(rowIndex) ? rowIndex : -1;
        if (!r?.length || !cols?.length || idx < 0 || idx >= r.length) return;
        const row = r[idx];
        if (!row?.id) return;
        const colDef = cols.find((c) => c.id === field);
        const editable = getIsEditable(colDef, row.original, colOptions);
        if (!editable) return;
        if (row.original) gridEvents.onBeforeEditCell?.(row.original, { field });
        setEditingCell({ rowId: row.id, field, initialKey: undefined });
      },
      stopEditCell: () => {
        setEditingCell({ rowId: null, field: null, initialKey: undefined });
      },
      // ---- Utility ----
      refresh: () => {
        const current = dataRef.current ?? [];
        setDataRef.current?.([...current]);
      },
      // ---- Tree 전용 (treeOptions 있을 때만 동작) ----
      /** 전체 펼치기 */
      expandAll: () => {
        if (!isTree || !treeOptions) return;
        const idKey = treeOptions.idKey ?? 'id';
        const childKey = treeOptions.childrenKey ?? 'subRows';
        const nodes = dataRef.current ?? [];
        const collect = (arr) => {
          const out = {};
          if (!arr?.length) return out;
          arr.forEach((n) => {
            const id = n[idKey] ?? n._tempId;
            if (id != null && n[childKey]?.length) {
              out[String(id)] = true;
              Object.assign(out, collect(n[childKey]));
            }
          });
          return out;
        };
        setExpanded(collect(nodes));
      },
      /** 전체 접기 */
      collapseAll: () => {
        if (!isTree) return;
        setExpanded({});
      },
      /** 선택 노드 펼치기 (expandChildren: true면 하위 노드도 모두 펼침) */
      expandSelected: (expandChildren = true) => {
        if (!isTree || !treeOptions) return;
        const rowId = focusedCell?.rowId;
        if (rowId == null) return;
        const idKey = treeOptions.idKey ?? 'id';
        const childKey = treeOptions.childrenKey ?? 'subRows';
        const nodes = dataRef.current ?? [];
        const collectUnder = (arr, targetId) => {
          const out = {};
          if (!arr?.length) return out;
          for (const n of arr) {
            const id = n[idKey] ?? n._tempId;
            const strId = id != null ? String(id) : null;
            if (strId === String(targetId)) {
              if (n[childKey]?.length) out[strId] = true;
              if (expandChildren) {
                const subCollect = (a) => {
                  if (!a?.length) return {};
                  const o = {};
                  a.forEach((node) => {
                    const sid = node[idKey] ?? node._tempId;
                    if (sid != null && node[childKey]?.length) {
                      o[String(sid)] = true;
                      Object.assign(o, subCollect(node[childKey]));
                    }
                  });
                  return o;
                };
                Object.assign(out, subCollect(n[childKey]));
              }
              return out;
            }
            const sub = collectUnder(n[childKey], targetId);
            if (Object.keys(sub).length) return sub;
          }
          return out;
        };
        const next = collectUnder(nodes, rowId);
        if (Object.keys(next).length) setExpanded((prev) => ({ ...prev, ...next }));
      },
      /** 선택 노드 접기 (collapseChildren: true면 하위 노드도 모두 접힘) */
      collapseSelected: (collapseChildren = true) => {
        if (!isTree || !treeOptions) return;
        const rowId = focusedCell?.rowId;
        if (rowId == null) return;
        const idKey = treeOptions.idKey ?? 'id';
        const childKey = treeOptions.childrenKey ?? 'subRows';
        const nodes = dataRef.current ?? [];
        const collectIds = new Set();
        const findAndCollect = (arr, targetId) => {
          if (!arr?.length) return false;
          for (const n of arr) {
            const id = n[idKey] ?? n._tempId;
            if (String(id) === String(targetId)) {
              collectIds.add(String(id));
              const walk = (a) => {
                if (!a?.length) return;
                a.forEach((node) => {
                  const sid = node[idKey] ?? node._tempId;
                  if (sid != null) collectIds.add(String(sid));
                  walk(node[childKey]);
                });
              };
              if (collapseChildren) walk(n[childKey]);
              return true;
            }
            if (findAndCollect(n[childKey], targetId)) return true;
          }
          return false;
        };
        findAndCollect(nodes, rowId);
        if (collectIds.size) setExpanded((prev) => {
          const next = { ...prev };
          collectIds.forEach((id) => delete next[id]);
          return next;
        });
      },
      /** 전체 체크. enableRowCheck 시 화면 행 기준 / 트리 checkboxField 시 데이터 필드 */
      checkAll: (checked) => {
        if (enableRowCheck) {
          checkAllVisibleRows(!!checked);
          return;
        }
        if (!isTree || !treeOptions?.checkboxField) return;
        const field = treeOptions.checkboxField;
        const val = checked ? (treeOptions.checkboxTrueValue ?? 'Y') : (treeOptions.checkboxFalseValue ?? 'N');
        const childKey = treeOptions.childrenKey ?? 'subRows';
        const setAll = (arr) => {
          if (!arr?.length) return arr;
          return arr.map((n) => ({ ...n, [field]: val, [childKey]: setAll(n[childKey]) }));
        };
        setDataRef.current?.(setAll(dataRef.current ?? []));
      },
      /** 체크된 행 데이터 배열 (flat) */
      getCheckedData: () => {
        if (enableRowCheck) return getCheckedOriginalRows();
        if (!isTree || !treeOptions?.checkboxField) return [];
        const nodes = dataRef.current ?? [];
        const childKey = treeOptions.childrenKey ?? 'subRows';
        const trueVal = treeOptions.checkboxTrueValue ?? 'Y';
        const flat = [];
        const walk = (arr) => {
          if (!arr?.length) return;
          arr.forEach((n) => {
            const { [childKey]: sub, ...rest } = n;
            flat.push(rest);
            walk(sub);
          });
        };
        walk(nodes);
        return flat.filter((row) => row[treeOptions.checkboxField] === trueVal);
      },
      clearRowChecks: () => clearRowChecks(),
    }),
    [focusedCell, setFocusedCell, setEditingCell, gridEvents, focusGrid, dataColumns, columns, ref?.gridEventsRef, isTree, treeOptions, setExpanded, getIsEditable, colOptions, findNodeInTree, setCellInTree, findDataIndexByRowId, enableRowCheck, checkAllVisibleRows, getCheckedOriginalRows, clearRowChecks]
  );



  const sizingInfo = table.getState().columnSizingInfo ?? {};
  const resizingColId = sizingInfo.isResizingColumn;
  const deltaOffset = sizingInfo.deltaOffset ?? 0;
  const headers = table.getHeaderGroups()[0]?.headers ?? [];
  const resizeLineLeft = resizingColId
    ? (() => {
        const idx = headers.findIndex((h) => h.column.id === resizingColId);
        if (idx < 0) return 0;
        const leftToColumn = headers.slice(0, idx).reduce((s, h) => s + h.getSize(), 0);
        return leftToColumn + headers[idx].getSize() + deltaOffset;
      })()
    : null;

  /** 헤더·바디 컬럼폭 동기화 (분리된 두 테이블이 동일 px를 쓰도록) */
  const tablePixelWidth = table.getTotalSize();
  const tableWidthStyle = fullWidth
    ? { width: '100%' }
    : { width: tablePixelWidth, minWidth: tablePixelWidth };

  const visibleLeafColumns = table.getVisibleLeafColumns?.() ?? [];

  const frozenColumnIdSet = useMemo(() => {
    const set = new Set();
    if (!enableFreeze) return set;
    // mod / rowCheck / rowNumber 은 항상 좌측 고정
    visibleLeafColumns.forEach((c) => {
      if (isHiGridSystemColumnId(c.id)) set.add(c.id);
    });
    if (freezeToColumnId) {
      for (const c of visibleLeafColumns) {
        set.add(c.id);
        if (c.id === freezeToColumnId) break;
      }
    }
    return set;
  }, [enableFreeze, freezeToColumnId, visibleLeafColumns]);

  const frozenLeftById = useMemo(() => {
    const map = {};
    let left = 0;
    visibleLeafColumns.forEach((c) => {
      if (frozenColumnIdSet.has(c.id)) {
        map[c.id] = left;
        left += c.getSize();
      }
    });
    return map;
  }, [visibleLeafColumns, frozenColumnIdSet]);

  const headerToneBg = headerActive
    ? theme.colors.grid.headerActiveBg
    : theme.colors.grid.headerBg;
  const headerToneFg = headerActive
    ? theme.colors.grid.headerActiveFg
    : theme.colors.grid.headerText;
  const headerSortFg = headerActive
    ? theme.colors.grid.headerActiveSort
    : theme.colors.primary;

  const getColBoxStyle = (column, { isHeader = false, rowSelected = false } = {}) => {
    const size = column.getSize();
    const base = fullWidth
      ? {
          width: size,
          minWidth: column.columnDef.minSize ?? size,
          boxSizing: 'border-box',
        }
      : {
          width: size,
          minWidth: size,
          maxWidth: size,
          boxSizing: 'border-box',
        };
    if (isHeader) {
      base.backgroundColor = headerToneBg;
      base.color = headerToneFg;
    }
    if (!frozenColumnIdSet.has(column.id)) return base;
    return {
      ...base,
      position: 'sticky',
      left: frozenLeftById[column.id] ?? 0,
      zIndex: isHeader ? 6 : 4,
      backgroundColor: isHeader
        ? headerToneBg
        : (rowSelected
          ? theme.colors.grid.rowSelected
          : (theme.colors.background?.white || theme.colors.white)),
      ...(isHeader ? { color: headerToneFg } : null),
    };
  };

  /** 바디 셀 — 행 높이 동기화 (스택 52 / 기본 28). rowSpan>1 이면 높이 고정 해제 */
  const getBodyCellStyle = (column, { rowSelected = false, isStack = false, rowSpan = 1 } = {}) => ({
    ...getColBoxStyle(column, { rowSelected }),
    ...(rowSpan > 1
      ? { height: undefined, maxHeight: undefined, verticalAlign: 'middle' }
      : { height: rowHeight, maxHeight: rowHeight }),
    padding: isStack ? 0 : undefined,
  });

  const openHeaderMenuAt = useCallback((e, columnId, columnLabel) => {
    if (!enableHeaderMenu) return;
    e.preventDefault();
    e.stopPropagation();
    setHeaderMenu({ x: e.clientX, y: e.clientY, columnId, columnLabel: columnLabel || columnId });
  }, [enableHeaderMenu]);

  const handleColumnDrop = useCallback(
    async (fromId, toId) => {
      if (!columnReorderEnabled || !fromId || !toId || fromId === toId) return;
      const order = columnOrder.length ? columnOrder : columns.map((c) => c.id);
      if (freezeToColumnId && wouldCrossFreezeBoundary(order, fromId, toId, freezeToColumnId)) {
        const ok = await showConfirm(
          '열이 고정되어 있어 해당 필드를 옮길 수 없습니다.\n열 고정을 해제하시겠습니까?',
          'warning',
          '고정 해제',
          '취소'
        );
        if (!ok) return;
        setFreezeToColumnId(null);
      }
      const next = moveColumnInOrder(order, fromId, toId);
      if (next) setColumnOrder(next);
    },
    [columnReorderEnabled, columnOrder, columns, freezeToColumnId]
  );

  const visibilityColumns = useMemo(() => {
    return dataColumns
      .filter((c) => c.visible !== false && c.meta?.visible !== false)
      .map((c) => {
        const id = c.id ?? c.accessorKey ?? c.field;
        return {
          id,
          label: c.header ?? id,
          visible: columnVisibility[id] !== false,
          locked: false,
        };
      })
      .filter((c) => c.id);
  }, [dataColumns, columnVisibility]);

  const filterChips = useMemo(
    () =>
      buildFilterChips({
        columnFilters,
        columns: dataColumns,
        rules: conditionalRules,
        formatRuleChip,
        formatListFilterChip,
      }),
    [columnFilters, dataColumns, conditionalRules]
  );

  /** 헤더 행 렌더 (고정 헤더·바디 테이블 공통). 헤더 클릭 시 정렬, 정렬된 컬럼은 오른쪽 끝에 ▲/▼ 표시 */
  const renderHeaderRow = (hg) => (
    <tr key={hg.id}>
      {hg.headers.map((h) => {
        const isResizing = h.column.getIsResizing?.();
        const canSort = h.column.getCanSort?.();
        const canFilter = columnFiltersEnabled && h.column.getCanFilter?.();
        const sortDir = h.column.getIsSorted?.(); // 'asc' | 'desc' | false
        const toggleSort = h.column.getToggleSortingHandler?.();
        const headerLabel = typeof h.column.columnDef.header === 'string'
          ? h.column.columnDef.header
          : h.column.id;
        const colBox = getColBoxStyle(h.column, { isHeader: true });
        const canReorder = columnReorderEnabled && !isHiGridSystemColumnId(h.column.id);
        return (
          <TanStackTh
            key={h.id}
            data-header-active={headerActive ? 'Y' : 'N'}
            draggable={canReorder}
            onDragStart={(e) => {
              if (!canReorder) {
                e.preventDefault();
                return;
              }
              headerDragRef.current = { from: h.column.id, moved: false };
              e.dataTransfer.setData('text/plain', h.column.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDrag={() => {
              if (headerDragRef.current.from) headerDragRef.current.moved = true;
            }}
            onDragOver={(e) => {
              if (!columnReorderEnabled) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const fromId = e.dataTransfer.getData('text/plain') || headerDragRef.current.from;
              handleColumnDrop(fromId, h.column.id);
            }}
            style={{
              position: frozenColumnIdSet.has(h.column.id) ? 'sticky' : 'relative',
              ...colBox,
              textAlign: h.column.columnDef.meta?.headerAlign ?? headerAlign,
              overflow: isResizing ? 'visible' : (h.column.id === 'rowCheck' ? 'visible' : undefined),
              // 행체크·mod 헤더는 좌우 패딩을 줄여 체크박스가 잘리지 않게
              ...(h.column.id === 'rowCheck' || h.column.id === 'mod'
                ? { paddingLeft: 4, paddingRight: 4 }
                : null),
              backgroundColor: isResizing
                ? (headerActive ? theme.colors.grid.headerActiveHover : theme.colors.grid.rowHover)
                : headerToneBg,
              color: headerToneFg,
              zIndex: isResizing ? 10 : (colBox.zIndex ?? 1),
              cursor: canReorder ? 'grab' : (canSort ? 'pointer' : undefined),
              userSelect: canSort || canReorder ? 'none' : undefined,
              top: 0,
            }}
            onClick={canSort && toggleSort ? (e) => {
              e.stopPropagation();
              if (headerDragRef.current.moved) {
                headerDragRef.current.moved = false;
                return;
              }
              toggleSort(e);
            } : undefined}
            onContextMenu={(e) => openHeaderMenuAt(e, h.column.id, headerLabel)}
          >
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: h.column.id === 'rowCheck' || h.column.id === 'mod' ? 'center' : undefined,
              width: '100%',
              gap: 4,
              paddingRight: (h.column.id === 'rowCheck' || h.column.id === 'mod' || h.column.id === 'rowNumber' || !h.column.getCanResize?.())
                ? 0
                : 10,
              color: headerToneFg,
            }}>
              <span style={{
                flex: h.column.id === 'rowCheck' || h.column.id === 'mod' || h.column.id === 'rowNumber' ? '0 0 auto' : 1,
                minWidth: 0,
                overflow: h.column.id === 'rowCheck' || h.column.id === 'rowNumber' ? 'visible' : 'hidden',
                textOverflow: 'ellipsis',
                color: headerToneFg,
                display: h.column.id === 'rowCheck' || h.column.id === 'rowNumber' ? 'inline-flex' : undefined,
                alignItems: h.column.id === 'rowCheck' || h.column.id === 'rowNumber' ? 'center' : undefined,
                justifyContent: h.column.id === 'rowCheck' || h.column.id === 'rowNumber' ? 'center' : undefined,
              }}>
                {(() => {
                  const sp = h.column.columnDef.meta?.stackPair;
                  if (!sp) return flexRender(h.column.columnDef.header, h.getContext());
                  const divColor = headerActive
                    ? 'rgba(255,255,255,0.55)'
                    : theme.colors.grid.border;
                  return (
                    <span style={{ display: 'flex', flexDirection: 'column', width: '100%', lineHeight: 1.25 }}>
                      <span style={{
                        padding: '2px 0',
                        borderBottom: `1px solid ${divColor}`,
                        textAlign: 'center',
                      }}
                      >
                        {sp.topLabel || sp.top}
                      </span>
                      <span style={{ padding: '2px 0', textAlign: 'center' }}>
                        {sp.bottomLabel || sp.bottom}
                      </span>
                    </span>
                  );
                })()}
              </span>
              {canSort && sortDir && (
                <span style={{ color: headerSortFg, fontSize: '0.9em', fontWeight: 700, flexShrink: 0 }} aria-hidden>
                  {sortDir === 'asc' ? '▲' : '▼'}
                </span>
              )}
              {canFilter && (
                <ExcelColumnFilterButton
                  column={h.column}
                  iconTone={headerActive ? 'onAccent' : 'default'}
                  conditionRule={conditionalRules.find((r) => r.columnId === h.column.id) || null}
                  onApplyCondition={(draft) => {
                    const rule = normalizeRule(
                      {
                        ...draft,
                        columnId: h.column.id,
                        header: headerLabel,
                        id: createRuleId(h.column.id),
                      },
                      h.column.columnDef.meta
                    );
                    setConditionalRules((prev) => {
                      const rest = (prev || []).filter((r) => r.columnId !== h.column.id);
                      return [...rest, rule];
                    });
                  }}
                  onClearCondition={() => {
                    setConditionalRules((prev) => (prev || []).filter((r) => r.columnId !== h.column.id));
                  }}
                />
              )}
            </span>
            {h.column.getCanResize?.() && (
              <div
                draggable={false}
                onDragStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); h.getResizeHandler()(e); }}
                onTouchStart={(e) => { h.getResizeHandler()(e); }}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  cursor: 'col-resize',
                  userSelect: 'none',
                }}
                aria-label="컬럼 너비 조절"
              />
            )}
          </TanStackTh>
        );
      })}
    </tr>
  );

  const theadContent = headerRows != null && headerRows.length > 0
    ? (() => {
        const headerGrid = buildHeaderGrid(headerRows, columns.length);
        if (!headerGrid) {
          return table.getHeaderGroups().map(renderHeaderRow);
        }
        return renderMergedHeaderRows(headerGrid, columns, headerAlign, TanStackTh);
      })()
    : table.getHeaderGroups().map(renderHeaderRow);

  const handleBodyScroll = useCallback((e) => {
    const sl = e.target.scrollLeft ?? 0;
    setScrollLeft(sl);
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = sl;
    if (footerScrollRef.current) footerScrollRef.current.scrollLeft = sl;
    // 세로 스크롤바 출현/소멸 시 헤더 패딩 동기화
    const el = e.target;
    const sbw = Math.max(0, el.offsetWidth - el.clientWidth);
    setBodyScrollbarWidth((prev) => (prev === sbw ? prev : sbw));
  }, []);

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const sbw = Math.max(0, el.offsetWidth - el.clientWidth);
      setBodyScrollbarWidth((prev) => (prev === sbw ? prev : sbw));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [data, rowHeight, columns, columnSizing, enableVirtual]);
  const handleHeaderScroll = useCallback((e) => {
    const sl = e.target.scrollLeft ?? 0;
    setScrollLeft(sl);
    if (gridWrapRef.current) gridWrapRef.current.scrollLeft = sl;
    if (footerScrollRef.current) footerScrollRef.current.scrollLeft = sl;
  }, []);
  const handleFooterScroll = useCallback((e) => {
    const sl = e.target.scrollLeft ?? 0;
    setScrollLeft(sl);
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = sl;
    if (gridWrapRef.current) gridWrapRef.current.scrollLeft = sl;
  }, []);

  /** 스크롤바(우측·하단) 근처일 때 data-scrollbar-hover → CSS에서 두께 확장 */
  const SCROLLBAR_HOVER_HIT = 16;
  const handleScrollbarHoverMove = useCallback((e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const hasV = el.scrollHeight > el.clientHeight + 1;
    const hasH = el.scrollWidth > el.clientWidth + 1;
    const nearV = hasV && x >= el.clientWidth - SCROLLBAR_HOVER_HIT;
    const nearH = hasH && y >= el.clientHeight - SCROLLBAR_HOVER_HIT;
    const next = nearV || nearH ? '1' : '0';
    if (el.getAttribute('data-scrollbar-hover') !== next) {
      el.setAttribute('data-scrollbar-hover', next);
    }
  }, []);
  const handleScrollbarHoverLeave = useCallback((e) => {
    e.currentTarget.setAttribute('data-scrollbar-hover', '0');
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: typeof height === 'number' ? `${height}px` : height, minHeight: 0 }}>
      {/* 고정 헤더 - 스크롤 영역 밖이라 세로 스크롤 시 움직이지 않음, 가로 스크롤바는 숨김(바디 스크롤로 동기화) */}
      <HiGridHeaderScrollArea
        ref={headerScrollRef}
        onScroll={handleHeaderScroll}
        data-header-active={headerActive ? 'Y' : 'N'}
        style={{
          flexShrink: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          /* 바디 세로 스크롤바 폭만큼 우측 여백 — 헤더·바디 열 정렬 */
          paddingRight: bodyScrollbarWidth || undefined,
          boxSizing: 'border-box',
          border: `1px solid ${theme.colors.grid.border}`,
          borderBottom: 'none',
          borderTopLeftRadius: theme.borderRadius.md,
          borderTopRightRadius: theme.borderRadius.md,
          background: headerActive ? theme.colors.grid.headerActiveBg : theme.colors.white,
        }}
      >
        <TanStackTable style={tableWidthStyle}>
          <thead style={table.getState().columnSizingInfo?.isResizingColumn ? { overflow: 'visible' } : undefined}>
            {theadContent}
          </thead>
          {frozenDisplayRows.length > 0 && (
            <tbody>
              {frozenDisplayRows.map((row) => (
                <TanStackTr
                  key={`fz-${row.id}`}
                  className={selectedRowIndex === row.index ? 'row-selected' : ''}
                  onClick={() => onRowClick(row.original, row.index)}
                  style={{
                    height: rowHeight,
                    backgroundColor: selectedRowIndex === row.index ? theme.colors.grid.rowSelected : theme.colors.grid.headerBg,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const mergeInfo = resolveBodyMerge(bodyMergeMap, row.index, cell.column.id);
                    if (mergeInfo.skip) return null;
                    const isFocused = isCellFocused(row.id, cell.column.id);
                    const isSelected = isCellInSelection(row.id, cell.column.id, rowIds, columnIds);
                    const isActiveCell = activeCell && row.id === activeCell.rowId && isStackFieldActive(cell.column, activeCell.field);
                    const showCopied = isSelected && showCopiedBorder;
                    const isCheckbox = cell.column.columnDef.meta?.type === 'checkbox';
                    const cellKey = isCheckbox ? `${cell.id}-${cell.getValue()}` : cell.id;
                    const rowSel = selectedRowIndex === row.index;
                    const isStack = !!cell.column.columnDef.meta?.stackPair;
                    return (
                      <TanStackTd
                        key={cellKey}
                        ref={isActiveCell ? activeCellRef : undefined}
                        rowSpan={mergeInfo.rowSpan}
                        className={`${!isStack && isFocused ? 'cell-focused' : ''} ${!isStack && isSelected ? 'cell-selected' : ''} ${showCopied ? 'cell-copied' : ''}`.trim()}
                        style={{
                          ...getBodyCellStyle(cell.column, {
                            rowSelected: rowSel && !isSelected,
                            isStack,
                            rowSpan: mergeInfo.rowSpan || 1,
                          }),
                          textAlign: cell.column.columnDef.meta?.align || 'left',
                          // 블럭 선택 셀은 CSS .cell-selected가 배경 담당 (선택 행색에 덮이지 않음)
                          ...(isSelected
                            ? getSelectionEdgeVars(row.id, cell.column.id, rowIds, columnIds)
                            : {
                                backgroundColor:
                                  getCellBackgroundColor(
                                    row.index,
                                    cell.column.columnDef,
                                    row.original,
                                    selectedRowIndex
                                  ) ||
                                  (rowSel
                                    ? theme.colors.grid.rowSelected
                                    : theme.colors.grid.headerBg),
                              }),
                        }}
                        onMouseDown={(e) => {
                          const tag = e.target?.tagName;
                          if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') { e.stopPropagation(); return; }
                          const spMeta = cell.column.columnDef.meta?.stackPair;
                          if (spMeta) {
                            const half = resolveStackHalfFromEvent(e, cell.column);
                            if (half) setStackFocus({ rowId: row.id, field: half });
                          } else {
                            setStackFocus(null);
                          }
                          handleCellMouseDown(e, row.id, cell.column.id);
                          // 편집 가능 셀은 인라인 에디터가 포커스를 가져감 — gridWrap 포커스 시 즉시 blur로 편집 종료됨
                          const colDef = columns.find((c) => c.id === cell.column.id) || cell.column.columnDef;
                          if (!getIsEditable(colDef, row.original, colOptions)) {
                            gridWrapRef.current?.focus();
                          }
                        }}
                        onContextMenu={(e) => openContextMenuAt(e, row.id, cell.column.id)}
                        onMouseEnter={() => handleCellMouseEnter(row.id, cell.column.id)}
                        onClick={(e) => {
                          const tag = e.target?.tagName;
                          if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') { e.stopPropagation(); return; }
                          onCellClick(e, row, cell.column, row.index);
                        }}
                        onDoubleClick={(e) => {
                          // 편집 중 INPUT/SELECT여도 행 더블클릭 콜백(팝업 등)은 동작해야 함
                          onCellDoubleClick(e, row, cell.column, row.index);
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TanStackTd>
                    );
                  })}
                </TanStackTr>
              ))}
            </tbody>
          )}
        </TanStackTable>
      </HiGridHeaderScrollArea>
      {/* 바디만 스크롤 */}
      <TanStackGridWrap
        ref={gridWrapRef}
        style={{ flex: 1, minHeight: 0, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
        tabIndex={0}
        onKeyDown={onWrapKeyDown}
        onScroll={handleBodyScroll}
        onMouseMove={handleScrollbarHoverMove}
        onMouseLeave={handleScrollbarHoverLeave}
        data-scrollbar-hover="0"
      >
        {enableVirtual ? (
        <div style={{ height: `${totalSize}px`, position: 'relative', width: fullWidth ? '100%' : tablePixelWidth }}>
        <TanStackTable style={tableWidthStyle}>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: 16, color: theme.colors.text.tertiary }}>
                조회된 자료가 없습니다.
              </td>
            </tr>
          ) : (
            virtualItems.map((virtualRow, indexInVirtual) => {
              const row = rows[virtualRow.index + safeFreezeRowCount];
              if (!row) return null;
              const rowSel = selectedRowIndex === row.index;
              return (
                <TanStackTr
                  key={row.id}
                  className={rowSel ? 'row-selected' : ''}
                  onClick={() => onRowClick(row.original, row.index)}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start - indexInVirtual * virtualRow.size}px)`,
                    backgroundColor: rowSel ? theme.colors.grid.rowSelected : undefined,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const mergeInfo = resolveBodyMerge(bodyMergeMap, row.index, cell.column.id);
                    if (mergeInfo.skip) return null;
                    const isFocused = isCellFocused(row.id, cell.column.id);
                    const isSelected = isCellInSelection(row.id, cell.column.id, rowIds, columnIds);
                    const isActiveCell = activeCell && row.id === activeCell.rowId && isStackFieldActive(cell.column, activeCell.field);
                    const showCopied = isSelected && showCopiedBorder;
                    const isCheckbox = cell.column.columnDef.meta?.type === 'checkbox';
                    const cellKey = isCheckbox ? `${cell.id}-${cell.getValue()}` : cell.id;
                    const isStack = !!cell.column.columnDef.meta?.stackPair;
                    return (
                      <TanStackTd
                        key={cellKey}
                        ref={isActiveCell ? activeCellRef : undefined}
                        rowSpan={mergeInfo.rowSpan}
                        className={`${!isStack && isFocused ? 'cell-focused' : ''} ${!isStack && isSelected ? 'cell-selected' : ''} ${showCopied ? 'cell-copied' : ''}`.trim()}
                        style={{
                          ...getBodyCellStyle(cell.column, {
                            rowSelected: rowSel && !isSelected,
                            isStack,
                            rowSpan: mergeInfo.rowSpan || 1,
                          }),
                          textAlign: cell.column.columnDef.meta?.align || 'left',
                          ...(isSelected
                            ? getSelectionEdgeVars(row.id, cell.column.id, rowIds, columnIds)
                            : {
                                backgroundColor:
                                  getCellBackgroundColor(
                                    row.index,
                                    cell.column.columnDef,
                                    row.original,
                                    selectedRowIndex
                                  ) ||
                                  getColBoxStyle(cell.column, { rowSelected: rowSel })
                                    .backgroundColor,
                              }),
                        }}
                        onMouseDown={(e) => {
                          const tag = e.target?.tagName;
                          if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') { e.stopPropagation(); return; }
                          const spMeta = cell.column.columnDef.meta?.stackPair;
                          if (spMeta) {
                            const half = resolveStackHalfFromEvent(e, cell.column);
                            if (half) setStackFocus({ rowId: row.id, field: half });
                          } else {
                            setStackFocus(null);
                          }
                          handleCellMouseDown(e, row.id, cell.column.id);
                          // 편집 가능 셀은 인라인 에디터가 포커스를 가져감 — gridWrap 포커스 시 즉시 blur로 편집 종료됨
                          const colDef = columns.find((c) => c.id === cell.column.id) || cell.column.columnDef;
                          if (!getIsEditable(colDef, row.original, colOptions)) {
                            gridWrapRef.current?.focus();
                          }
                        }}
                        onContextMenu={(e) => openContextMenuAt(e, row.id, cell.column.id)}
                        onMouseEnter={() => handleCellMouseEnter(row.id, cell.column.id)}
                        onClick={(e) => {
                          const tag = e.target?.tagName;
                          if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') { e.stopPropagation(); return; }
                          onCellClick(e, row, cell.column, row.index);
                        }}
                        onDoubleClick={(e) => {
                          // 편집 중 INPUT/SELECT여도 행 더블클릭 콜백(팝업 등)은 동작해야 함
                          onCellDoubleClick(e, row, cell.column, row.index);
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TanStackTd>
                    );
                  })}
                </TanStackTr>
              );
            })
          )}
        </tbody>
        </TanStackTable>
        </div>
        ) : (
        <TanStackTable style={tableWidthStyle}>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: 16, color: theme.colors.text.tertiary }}>
                조회된 자료가 없습니다.
              </td>
            </tr>
          ) : (
            scrollDisplayRows.map((row) => {
              const rowSel = !isTree && selectedRowIndex === row.index;
              return (
              <TanStackTr
                key={row.id}
                className={rowSel ? 'row-selected' : ''}
                onClick={() => onRowClick(row.original, row.index)}
                style={{
                  height: rowHeight,
                  backgroundColor: isTree ? undefined : (rowSel ? theme.colors.grid.rowSelected : undefined),
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const mergeInfo = resolveBodyMerge(bodyMergeMap, row.index, cell.column.id);
                  if (mergeInfo.skip) return null;
                  const isFocused = isCellFocused(row.id, cell.column.id);
                  const isSelected = isCellInSelection(row.id, cell.column.id, rowIds, columnIds);
                  const isActiveCell = activeCell && row.id === activeCell.rowId && isStackFieldActive(cell.column, activeCell.field);
                  const showCopied = isSelected && showCopiedBorder;
                  const isCheckbox = cell.column.columnDef.meta?.type === 'checkbox';
                  const cellKey = isCheckbox ? `${cell.id}-${cell.getValue()}` : cell.id;
                  const isStack = !!cell.column.columnDef.meta?.stackPair;
                  return (
                    <TanStackTd
                      key={cellKey}
                      ref={isActiveCell ? activeCellRef : undefined}
                      rowSpan={mergeInfo.rowSpan}
                      className={`${!isStack && isFocused ? 'cell-focused' : ''} ${!isStack && isSelected ? 'cell-selected' : ''} ${showCopied ? 'cell-copied' : ''}`.trim()}
                      style={{
                        ...getBodyCellStyle(cell.column, {
                          rowSelected: rowSel && !isSelected,
                          isStack,
                          rowSpan: mergeInfo.rowSpan || 1,
                        }),
                        textAlign: cell.column.columnDef.meta?.align || 'left',
                        ...(isSelected
                          ? getSelectionEdgeVars(row.id, cell.column.id, rowIds, columnIds)
                          : {
                              backgroundColor:
                                getCellBackgroundColor(
                                  row.index,
                                  cell.column.columnDef,
                                  row.original,
                                  isTree ? null : selectedRowIndex
                                ) ||
                                getColBoxStyle(cell.column, { rowSelected: rowSel })
                                  .backgroundColor,
                            }),
                      }}
                      onMouseDown={(e) => {
                        const tag = e.target?.tagName;
                        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') { e.stopPropagation(); return; }
                        const spMeta = cell.column.columnDef.meta?.stackPair;
                        if (spMeta) {
                          const half = resolveStackHalfFromEvent(e, cell.column);
                          if (half) setStackFocus({ rowId: row.id, field: half });
                        } else {
                          setStackFocus(null);
                        }
                        handleCellMouseDown(e, row.id, cell.column.id);
                        if (isTree) setSelectionRange(null);
                        const colDef = columns.find((c) => c.id === cell.column.id) || cell.column.columnDef;
                        if (!getIsEditable(colDef, row.original, colOptions)) {
                          gridWrapRef.current?.focus();
                        }
                      }}
                      onContextMenu={(e) => openContextMenuAt(e, row.id, cell.column.id)}
                      onMouseEnter={() => handleCellMouseEnter(row.id, cell.column.id)}
                      onClick={(e) => {
                        const tag = e.target?.tagName;
                        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') { e.stopPropagation(); return; }
                        onCellClick(e, row, cell.column, row.index);
                      }}
                      onDoubleClick={(e) => {
                        // 편집 중 INPUT/SELECT여도 행 더블클릭 콜백(팝업 등)은 동작해야 함
                        onCellDoubleClick(e, row, cell.column, row.index);
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TanStackTd>
                  );
                })}
              </TanStackTr>
            );})
          )}
        </tbody>
        </TanStackTable>
        )}
        {resizeLineLeft != null && (
        <div
          style={{
            position: 'absolute',
            left: resizeLineLeft - scrollLeft,
            top: 0,
            bottom: 0,
            width: 3,
            background: theme.colors.primary,
            opacity: 0.8,
            pointerEvents: 'none',
            zIndex: 20,
          }}
          aria-hidden
        />
        )}
      </TanStackGridWrap>
      <GridFilterStatusBar
        chips={filterChips}
        onRemove={(chip) => {
          if (chip.type === 'list') {
            setColumnFilters((prev) => (prev || []).filter((f) => f.id !== chip.columnId));
          } else {
            setConditionalRules((prev) => (prev || []).filter((r) => r.id !== chip.id));
          }
        }}
        onClearAll={() => {
          setColumnFilters([]);
          setConditionalRules([]);
        }}
      />
      {/* 고정 푸터 - 헤더처럼 스크롤 영역 밖에 두어 세로 스크롤 시 항상 하단 고정, 가로 스크롤은 바디와 동기화 */}
      {(() => {
        const footerGroups = table.getFooterGroups?.() ?? [];
        const footerRow = footerGroups[0];
        const hasFooter = footerRow?.headers?.some((h) => h.column.columnDef.footer);
        if (!hasFooter) return null;
        return (
          <HiGridHeaderScrollArea
            ref={footerScrollRef}
            onScroll={handleFooterScroll}
            style={{
              flexShrink: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              paddingRight: bodyScrollbarWidth || undefined,
              boxSizing: 'border-box',
              border: `1px solid ${theme.colors.border}`,
              borderTop: 'none',
              borderBottomLeftRadius: theme.borderRadius.sm,
              borderBottomRightRadius: theme.borderRadius.sm,
              background: theme.colors.background.light,
            }}
          >
            <TanStackTable style={tableWidthStyle}>
              <tfoot>
                <TanStackTr>
                  {footerRow.headers.map((header) => (
                    <TanStackTd
                      key={header.id}
                      style={{
                        ...getColBoxStyle(header.column),
                        textAlign: header.column.columnDef.meta?.align || 'left',
                        fontWeight: 600,
                        padding: '6px 8px',
                      }}
                    >
                      {header.column.columnDef.footer
                        ? flexRender(header.column.columnDef.footer, header.getContext())
                        : ''}
                    </TanStackTd>
                  ))}
                </TanStackTr>
              </tfoot>
            </TanStackTable>
          </HiGridHeaderScrollArea>
        );
      })()}
      {contextMenu && contextMenuItems.length > 0 && (
        <GridContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenuItems}
          onSelect={handleContextMenuSelect}
          onClose={closeContextMenu}
        />
      )}
      {enableHeaderMenu && headerMenu && (
        <GridHeaderContextMenu
          x={headerMenu.x}
          y={headerMenu.y}
          columnId={headerMenu.columnId}
          columnLabel={headerMenu.columnLabel}
          freezeToColumnId={freezeToColumnId}
          freezeRowCount={freezeRowCount}
          canFreezeRows={focusedCell?.rowId != null && !isTree}
          enableUserMerge={enableUserMerge}
          enablePivot={enablePivot}
          onOpenMerge={() => {
            setMergePopup({ x: headerMenu.x, y: headerMenu.y });
          }}
          onOpenPivot={() => setPivotOpen(true)}
          onOpenVisibility={() => {
            setColumnVisPopup({ x: headerMenu.x, y: headerMenu.y });
          }}
          onFreezeToHere={() => {
            if (!isHiGridSystemColumnId(headerMenu.columnId)) {
              setFreezeToColumnId(headerMenu.columnId);
            }
          }}
          onClearColumnFreeze={() => setFreezeToColumnId(null)}
          onFreezeRowsToHere={() => {
            const idx = rows.findIndex((r) => r.id === focusedCell?.rowId);
            if (idx >= 0) setFreezeRowCount(idx + 1);
          }}
          onClearRowFreeze={() => setFreezeRowCount(0)}
          onClose={() => setHeaderMenu(null)}
        />
      )}
      {enableHeaderMenu && columnVisPopup && (
        <GridColumnVisibilityPopup
          x={columnVisPopup.x}
          y={columnVisPopup.y}
          columns={visibilityColumns}
          freezeToColumnId={freezeToColumnId}
          freezeRowCount={freezeRowCount}
          onToggle={(id) => {
            setColumnVisibility((prev) => ({
              ...prev,
              [id]: prev[id] === false ? true : false,
            }));
          }}
          onShowAll={() => {
            setColumnVisibility({});
          }}
          onClearColumnFreeze={() => setFreezeToColumnId(null)}
          onClearRowFreeze={() => setFreezeRowCount(0)}
          onClose={() => setColumnVisPopup(null)}
        />
      )}
      {enableContextMenu && enableFindReplace && (
        <GridFindReplaceDialog
          open={findReplaceOpen}
          onClose={closeFindReplaceDialog}
          initialFind={
            focusedCell?.field && focusedCell?.rowId != null
              ? String(
                  rows.find((r) => r.id === focusedCell.rowId)?.original?.[focusedCell.field] ?? ''
                )
              : ''
          }
          currentField={focusedCell?.field && !isHiGridSystemColumnId(focusedCell.field) ? focusedCell.field : ''}
          columnOptions={findColumnOptions}
          allowReplace={allowReplace}
          initialScope={findReplaceMeta.initialScope}
          hasSelectionSnapshot={findReplaceMeta.hasSelectionSnapshot}
          onFindNext={handleFindNext}
          onReplace={handleReplaceOne}
          onReplaceAll={handleReplaceAll}
        />
      )}
      {enableUserMerge && mergePopup && (
        <GridMergeColumnsPopup
          x={mergePopup.x}
          y={mergePopup.y}
          columns={visibilityColumns}
          enabled={mergeEnabled}
          selectedIds={mergeColumnIds}
          onChange={({ enabled, columnIds }) => {
            setMergeTouched(true);
            setMergeEnabled(enabled);
            setMergeColumnIds(columnIds);
          }}
          onClose={() => setMergePopup(null)}
        />
      )}
      {enablePivot && (
        <GridPivotDialog
          open={pivotOpen}
          onClose={() => setPivotOpen(false)}
          fields={(dataColumns || []).map((c) => ({
            id: c.id ?? c.accessorKey ?? c.field,
            label: c.header,
            kind: inferColumnKind(c.meta),
          }))}
          rows={(rows || []).map((r) => r.original).filter(Boolean)}
          prefsUserId={prefsUserId || ''}
          gridId={gridId || ''}
        />
      )}
    </div>
  );
});

HiGridComponent.displayName = 'HiGridComponent';

export default HiGridComponent;
